import { useEffect, useMemo, useState, useCallback, forwardRef, useRef } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import LocalPharmacyOutlinedIcon from "@mui/icons-material/LocalPharmacyOutlined";
import BiotechOutlinedIcon from "@mui/icons-material/BiotechOutlined";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import { toast } from "react-toastify";
import {
  searchChargeCaptureServices,
  investigationCategoryToGroupCode,
} from "../../../ipd-billing/billing-api";
import {
  createLabOrderBatchId,
  rememberLabOrderBatch,
} from "../../../lab-technician/labOrdersShared";
import usePharmacyStore from "../../hooks/usePharmacyStore";
import { totalQuantity } from "../../../../utils/medicationOrder";

const toLabel = (value = "") =>
  value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

// Medication order's "instructions" field is a dropdown (not free text) using the
// same meal-timing options as the Investigation tab's test_items.instruction column.
const MEDICATION_INSTRUCTION_OPTIONS = [
  "Before Breakfast",
  "After Breakfast",
  "With Breakfast",
  "Before Lunch",
  "After Lunch",
  "With Lunch",
  "Before Dinner",
  "After Dinner",
  "With Dinner",
  "Before Food",
  "After Food",
  "With Food",
  "Empty Stomach",
  "Fasting",
  "Bedtime",
  "Morning",
  "Afternoon",
  "Evening",
  "Night",
  "As Needed",
  "Immediately",
  "Before Procedure",
  "After Procedure",
];

const getDefaultFieldValue = (field) => {
  if (field.type === "multi-select") return [];
  if (field.type === "table") return []; // start empty; user adds rows
  if (field.type === "boolean") return false;
  if (field.type === "number") return "";
  if (field.type === "object" || field.type === "group") {
    return buildInitialValues(field.structure || []);
  }
  return "";
};

const buildInitialValues = (fields = []) =>
  fields.reduce((acc, field) => {
    if (!field?.field) return acc;
    acc[field.field] = getDefaultFieldValue(field);
    return acc;
  }, {});

const getNestedValue = (source, path) =>
  path.reduce(
    (current, key) => (current && typeof current === "object" ? current[key] : undefined),
    source
  );

const updateNestedValue = (source, path, value) => {
  const cloned = Array.isArray(source) ? [...source] : { ...source };
  let cursor = cloned;

  for (let index = 0; index < path.length - 1; index += 1) {
    const key = path[index];
    const nextValue = cursor[key] && typeof cursor[key] === "object" ? { ...cursor[key] } : {};
    cursor[key] = nextValue;
    cursor = nextValue;
  }

  cursor[path[path.length - 1]] = value;
  return cloned;
};

const pad2 = (n) => String(n).padStart(2, "0");

/** Parse API / form string to Date for picker; returns null if empty/invalid */
const parseDateValue = (raw) => {
  if (raw == null || raw === "") return null;
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) return raw;
  if (typeof raw !== "string") return null;
  const s = raw.trim();
  if (!s) return null;
  const isoDate = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
  if (isoDate) {
    const [, yyyy, mm, dd, hh, min] = isoDate;
    const d = new Date(
      Number(yyyy),
      Number(mm) - 1,
      Number(dd),
      hh != null ? Number(hh) : 0,
      min != null ? Number(min) : 0,
      0,
      0
    );
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (s.includes("T")) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const m = s.match(/^(\d{2})-(\d{2})-(\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/);
  if (m) {
    const [, dd, mm, yyyy, hh, min] = m;
    const d = new Date(
      Number(yyyy),
      Number(mm) - 1,
      Number(dd),
      hh != null ? Number(hh) : 0,
      min != null ? Number(min) : 0,
      0,
      0
    );
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d2 = new Date(s);
  return Number.isNaN(d2.getTime()) ? null : d2;
};

const formatDateForApi = (date, includeTime) => {
  if (!date || Number.isNaN(date.getTime())) return "";
  if (includeTime) {
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(
      date.getHours()
    )}:${pad2(date.getMinutes())}:00`;
  }
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

const formatTimeForApi = (date) => {
  if (!date || Number.isNaN(date.getTime())) return "";
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
};

const isEmptyValue = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  if (typeof value === "number") return Number.isNaN(value) || value === "";
  if (typeof value === "boolean") return value === false;
  if (Array.isArray(value)) return value.every((item) => isEmptyValue(item));
  if (typeof value === "object") {
    return Object.values(value).every((nestedValue) => isEmptyValue(nestedValue));
  }
  return false;
};

// [VALIDATION] Mirrors care_plan_structures "required": true — used on Add To Activity + Save.
const isFieldRequired = (field) => field?.required === true;

const isRequiredValueMissing = (value, field) => {
  if (!isFieldRequired(field)) return false;
  if (field.type === "boolean") return value !== true;
  if (field.type === "multi-select") return !Array.isArray(value) || value.length === 0;
  if (field.type === "number") {
    return value === "" || value === null || value === undefined || Number.isNaN(Number(value));
  }
  return isEmptyValue(value);
};

const collectRequiredFieldDefs = (structureFields = [], pathPrefix = []) => {
  const requiredFields = [];
  structureFields.forEach((field) => {
    if (!field?.field) return;
    const path = [...pathPrefix, field.field];
    if (field.type === "object") {
      requiredFields.push(...collectRequiredFieldDefs(field.structure || [], path));
      return;
    }
    if (isFieldRequired(field)) {
      requiredFields.push({ field, path, label: toLabel(field.field) });
    }
  });
  return requiredFields;
};

const validateEntryRequiredFields = (entry, structureFields = []) => {
  const errors = {};
  collectRequiredFieldDefs(structureFields).forEach(({ field, path, label }) => {
    const value = getNestedValue(entry, path);
    if (isRequiredValueMissing(value, field)) {
      errors[path.join(".")] = `${label} is required`;
    }
  });
  return errors;
};

const getEntryDisplayLabel = (entry, activityNameRaw = "") => {
  const activityName = String(activityNameRaw || "").toLowerCase().trim();

  if (activityName === "medication") {
    const med = entry?.medication || {};
    const name = String(med?.drug_name || med?.name || "").trim();
    const frequency =
      med?.frequency !== null && med?.frequency !== undefined && String(med.frequency).trim() !== ""
        ? String(med.frequency).trim()
        : "";

    if (name && frequency) return `${name} • Frequency: ${frequency}`;
    if (name) return `${name} • Frequency: -`;
    if (frequency) return `Medication • Frequency: ${frequency}`;
    return "Medication • Frequency: -";
  }

  if (activityName === "task") {
    const taskName = String(entry?.task || entry?.title || entry?.name || "").trim();
    const frequency = String(entry?.frequency || "").trim();
    if (taskName && frequency) return `${taskName} • Frequency: ${frequency}`;
    if (taskName) return `${taskName} • Frequency: -`;
    if (frequency) return `Task • Frequency: ${frequency}`;
    return "Task • Frequency: -";
  }

  if (activityName === "test") {
    const testName = String(entry?.testName || entry?.orderDetail || entry?.name || "").trim();
    const priority = String(entry?.priority || "").trim();
    if (testName && priority) return `${testName} • Priority: ${priority}`;
    if (testName) return `${testName} • Priority: -`;
    if (priority) return `Test • Priority: ${priority}`;
    return "Test • Priority: -";
  }

  return `${toLabel(activityNameRaw || "Entry")}`;
};

const displayOrDash = (value) => {
  if (value === null || value === undefined) return "-";
  const text = String(value).trim();
  return text ? text : "-";
};

const getTableConfigForActivity = (activityNameRaw = "") => {
  const activityName = String(activityNameRaw || "").toLowerCase().trim();

  if (activityName === "medication") {
    return {
      columns: [
        { key: "name", label: "Medicine Name" },
        { key: "frequency", label: "Frequency" },
        { key: "duration", label: "Duration" },
      ],
      getRow: (entry) => {
        const med = entry?.medication || {};
        const dur = displayOrDash(med?.duration);
        const durUnit = displayOrDash(med?.duration_unit);
        return {
          name: displayOrDash(med?.drug_name || med?.name),
          frequency: displayOrDash(med?.frequency),
          duration:
            dur === "-" && durUnit === "-"
              ? "-"
              : `${dur !== "-" ? dur : ""}${durUnit !== "-" ? ` ${durUnit}` : ""}`.trim(),
        };
      },
    };
  }

  if (activityName === "task") {
    return {
      columns: [
        { key: "taskNo", label: "Task No." },
        { key: "frequency", label: "Frequency" },
        { key: "priority", label: "Priority" },
        { key: "notes", label: "Notes" },
      ],
      getRow: (entry, index) => ({
        taskNo: String(index + 1),
        frequency: displayOrDash(entry?.frequency),
        priority: displayOrDash(entry?.priority),
        notes: displayOrDash(entry?.notes),
      }),
    };
  }

  if (activityName === "test") {
    return {
      columns: [
        { key: "testName", label: "Test Name" },
        { key: "priority", label: "Priority" },
        { key: "frequency", label: "Frequency" },
        { key: "scheduledDateTime", label: "Scheduled" },
      ],
      getRow: (entry) => ({
        testName: displayOrDash(entry?.testName || entry?.orderDetail || entry?.name),
        priority: displayOrDash(entry?.priority),
        frequency: displayOrDash(entry?.frequency),
        scheduledDateTime: displayOrDash(entry?.scheduledDateTime || entry?.occurrenceDateTime),
      }),
    };
  }

  return null;
};

/** HH:mm stored for time-only fields */
const parseTimeFieldValue = (raw) => {
  if (raw == null || String(raw).trim() === "") return null;
  const s = String(raw).trim();
  const hm = s.match(/^(\d{1,2}):(\d{2})$/);
  if (hm) {
    const d = new Date(2000, 0, 1, Number(hm[1]), Number(hm[2]), 0, 0);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(`2000-01-01T${s}`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const DatePickerTextField = forwardRef(
  ({ label, value, onClick, disabled, endIcon, ...rest }, ref) => {
    const { InputProps: restInputProps = {}, inputProps: restFieldInputProps, sx: restSx, ...tfRest } =
      rest;
    return (
      <TextField
        {...tfRest}
        inputRef={ref}
        fullWidth
        size="small"
        label={label}
        value={value || ""}
        onClick={onClick}
        disabled={disabled}
        InputLabelProps={{ shrink: true }}
        inputProps={{ readOnly: true, ...restFieldInputProps }}
        InputProps={{
          ...restInputProps,
          endAdornment: (
            <InputAdornment
              position="end"
              onClick={(e) => {
                if (disabled) return;
                e.stopPropagation();
                onClick?.(e);
              }}
              onMouseDown={(e) => {
                if (disabled) return;
                e.preventDefault();
              }}
              sx={{
                color: "action.active",
                ml: 0,
                cursor: disabled ? "default" : "pointer",
              }}
            >
              {endIcon}
            </InputAdornment>
          ),
        }}
        sx={[{ cursor: disabled ? "default" : "pointer" }, restSx].filter(Boolean)}
      />
    );
  }
);
DatePickerTextField.displayName = "DatePickerTextField";

const DateTimeFieldEndIcon = () => (
  <Box
    component="span"
    sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}
    aria-hidden
  >
    <CalendarTodayIcon sx={{ fontSize: 18 }} />
    <AccessTimeIcon sx={{ fontSize: 18 }} />
  </Box>
);

const CARE_PLAN_DATE_PICKER_POPPER = {
  strategy: "fixed",
  modifiers: [
    {
      name: "zIndex",
      enabled: true,
      phase: "write",
      fn({ state }) {
        state.styles.popper.zIndex = 99999;
      },
    },
    { name: "preventOverflow", options: { padding: 8 } },
  ],
};

/*
 * ── CHARGE MASTER + BILLING FLOW (CarePlanActivityModal) ─────────────────────
 *
 * Grep for "[BILLING]" in this file to find every integration point.
 *
 * Flow overview:
 * 1. getChargeSearchConfig     — decides which fields get charge-master autocomplete
 * 2. fetchChargeMasterOptions  — GET search_charge_master/ for Test, search_medicines/
 *                                 for Medication (debounced in step 3)
 * 3. scheduleChargeMasterSearch — 1000ms debounce before calling step 2
 * 4. renderField Autocomplete    — Medication "name" / Test "orderDetail" UI
 * 5. setBillingChargeSelection   — stores service_code on entry as __billing_charge
 *                                  (Medication results have no service_code, so
 *                                  medication entries no longer populate this)
 * 6. handleAddToActivity         — __billing_charge travels with each queued entry
 * 7. handleSubmit                — strips __billing_charge for care_plans API, then
 *                                  calls postBillingCharges after onSubmit succeeds
 * 8. postBillingCharges          — single: billing/charges/ | multi: bulk-post/
 *
 * Parent wiring: PatientDetailsPanel passes admissionId prop (required for billing).
 * To disable billing entirely, remove the postBillingCharges call in handleSubmit.
 * To change which fields search, edit getChargeSearchConfig.
 * To change API endpoints/payloads, edit fetchChargeMasterOptions + postBillingCharges.
 * billing/charges/ payout extras (buildSingleBillingChargePayload):
 *   - Both cases → source_module: "IPD"
 *   - Referred To Me → referred_by_provider_id, payout_eligible, calculate_doctor_payout
 *   - Admitting list → calculate_doctor_payout only
 * Parent passes isReferredToMePatient + referredByProviderId from referred_to_me response.
 *
 * ── LAB TEST FLOW (Test activity only) ───────────────────────────────────────
 * Grep for "[LAB_TEST]" in this file to find every integration point.
 *
 * Flow overview:
 * 1. PatientDetailsPanel passes careContextId + doctorName from ipd_admission list
 *    (admission_details.care_context_ref, doctor_name — no extra admission fetch).
 * 2. setBillingChargeSelection stores charge-master fields on __billing_charge for lab payload.
 * 3. buildLabTestsFromEntries — maps __billing_charge → add_patient_lab_tests test[] items.
 * 4. postPatientLabTests — POST add_patient_lab_tests/ after care plan save
 *    (standalone Test activity + Orders → Investigation tab).
 *
 * To disable lab test posting, remove the postPatientLabTests call in handleSubmit.
 * To change payload fields, edit postPatientLabTests.
 *
 * ── PHARMACY PRESCRIPTION FLOW — none, deliberately ──────────────────────────
 * This modal does NOT post add_ipd_prescription/. submit_care_plan/ already
 * routes every medication entry through create_medication_order server-side
 * (main/ipd/submit_care_plan.py), which writes the order-book row AND schedules
 * the dose/eMAR tasks. Posting the prescription from here as well produced TWO
 * rows and two order numbers for one click — observed in the tenant ~300ms
 * apart, one carrying care_plan_data_id and one not — so the pharmacy saw every
 * nurse order twice and only the stray copy carried the patient's allergies.
 *
 * The patient's allergies now ride on the order from the backend, resolved from
 * the latest nursing assessment in medication_order.patient_allergies(). That is
 * why this component takes no patientId/diagnosis/allergies props: it has no
 * prescription payload left to build.
 *
 * NOTE: the doctor's CarePlanActivityModal still posts add_ipd_prescription/ on
 * top of care_plans/, which calls create_medication_order too — the same double
 * write, untouched here on purpose so no doctor flow regresses.
 */

// [BILLING] Debounce delay (ms) before search_charge_master/ is called after typing.
const CHARGE_SEARCH_DEBOUNCE_MS = 1000;

// [LAB_TEST] The lab and radiology dashboards match priority by exact string (their
// Priority filters compare `o.priority !== priorityFilter` against "Routine" / "Urgent" /
// "STAT"), while care_plan_structures supplies its options lowercase. Normalize on the way
// out so an order placed here is filterable there. Empty → "Routine", the same default
// both dashboards fall back to when the field is missing.
const toWirePriority = (raw) => {
  const value = String(raw || "").trim();
  if (!value) return "Routine";
  const lower = value.toLowerCase();
  if (lower === "stat" || lower === "asap") return lower.toUpperCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

// [PHARMACY_RX] search_medicines/ terms carry dose + form ("Calpol 1 g oral tablet"),
// which the inventory catalogue doesn't index — strip from the first digit onward so
// the stock lookup searches on the brand/molecule name alone ("Calpol").
const stripDoseForm = (name) => String(name || "").replace(/\d.*$/, "").trim() || String(name || "").trim();

// [PHARMACY_RX] search_medicines/ writes the composition with the strength in
// brackets ("Paracetamol (500mg)"); the inventory catalogue spells the same
// thing flat ("Paracetamol 500mg"). Drop the brackets so the substitute lookup
// searches on a term the catalogue can actually match, keeping the strength so
// only the same composition comes back.
const normalizeGenericTerm = (raw) =>
  String(raw || "")
    .replace(/[()]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

// [PHARMACY_RX] prescribe/search does NOT return a has_stock flag — availability
// has to be read off the quantity fields ("total_available_quantity": "0",
// batch_count 0, batches []). Reading a missing has_stock as "in stock" is what
// kept the Out of Stock badge and Substitute button from ever appearing.
// Quantities arrive as strings, and null means "unknown" (not zero), so only a
// value we can actually parse decides the answer; null propagates as unknown.
const resolveItemHasStock = (item) => {
  if (typeof item?.has_stock === "boolean") return item.has_stock;
  const rawQty =
    item?.total_available_quantity ?? item?.available_quantity ?? item?.stock;
  if (rawQty !== null && rawQty !== undefined && rawQty !== "") {
    const qty = Number(rawQty);
    if (Number.isFinite(qty)) return qty > 0;
  }
  if (typeof item?.can_fulfill === "boolean") return item.can_fulfill;
  if (Array.isArray(item?.batches)) return item.batches.length > 0;
  return null;
};

// [PHARMACY_RX] GET inventory/pharmacy/prescribe/search/ — pass q for a brand/generic/
// name lookup, or genericName instead to list in-stock substitutes for an OOS brand.
// Normalizes to { name, item_id, generic_name, has_stock } so the Autocomplete's
// item.name lookup keeps working regardless of which param drove the search.
// `locationId` is the selected pharmacy store (usePharmacyStore) — stock is per
// store, so without it the answer isn't about any real counter.
async function fetchPharmacyMedicationOptions(getApiHeaders, { q, genericName, locationId } = {}) {
  const params = { limit: 20 };
  if (q) params.q = q;
  if (genericName) {
    params.generic_name = genericName;
    params.available_only = true;
  }
  if (locationId) params.location_id = locationId;
  const response = await axios.get(
    `${process.env.REACT_APP_BACKEND_ROUTE_URL}inventory/pharmacy/prescribe/search/`,
    { headers: getApiHeaders(), params }
  );
  const payload = response?.data?.data ?? response?.data ?? [];
  const rows = Array.isArray(payload) ? payload : (payload.results || payload.items || payload.data || []);
  return rows.map((item) => {
    const id = item?.item_id ?? item?.id ?? item?.inventory_item_id ?? "";
    const name = item?.item_name || item?.name || item?.brand_name || item?.generic_name || "";
    if (!name) return null;
    const generic = String(item?.generic_name || "").trim();
    // Brand with the composition in brackets — "Crocin 500 Advance (Paracetamol
    // 500mg)" — the same shape search_medicines/ terms already use, so a doctor
    // reads a substitute the same way as any other drug name. Skipped when the
    // brand already spells the composition out, to avoid a doubled-up label.
    const showGeneric = generic && !name.toLowerCase().includes(generic.toLowerCase());
    return {
      name,
      label: showGeneric ? `${name} (${generic})` : name,
      item_id: id,
      medical_service_id: id,
      generic_name: generic,
      has_stock: resolveItemHasStock(item),
      _raw: item,
    };
  }).filter(Boolean);
}

// [BILLING] Maps activity + field name → charge type for search_charge_master/.
// Change here if Medication/Test should search on different fields or types.
const getChargeSearchConfig = (field, activityName, pathPrefix = []) => {
  const fieldName = String(field?.field || "").toLowerCase();

  // Medication drug name — works for both the standalone "medication" activity and
  // the "orders" modal where drug_name is nested inside medication_order.medication
  const isMedDrugField = fieldName === "name" || fieldName === "drug_name";
  const isMedContext =
    activityName === "medication" ||
    (activityName === "orders" && pathPrefix.includes("medication_order"));
  if (isMedDrugField && isMedContext) return { type: "MEDICATION" };

  if (activityName === "test" && (fieldName === "name" || fieldName === "orderdetail" || fieldName === "order_detail")) {
    return { type: "TEST" };
  }
  return null;
};

// [BILLING] Resolves quantity for billing payload — uses frequency field, else 1.
const parseBillingQuantity = (entry, activityName) => {
  let frequency = "";
  if (activityName === "medication") {
    frequency = entry?.medication?.frequency ?? entry?.frequency ?? "";
  } else {
    frequency = entry?.frequency ?? "";
  }
  const parsed = Number(String(frequency).trim());
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

// [BILLING] Remarks sent to billing/charges/ — sourced from the notes field.
const getBillingRemarks = (entry) => String(entry?.notes ?? "").trim();

// [BILLING] Single charge payload for ipd_admission/{id}/billing/charges/ — payout fields vary by patient source.
const buildSingleBillingChargePayload = ({
  entry,
  activityName,
  isReferredToMePatient,
  referredByProviderId,
}) => {
  const charge = entry.__billing_charge || {};
  const payload = {
    quantity: parseBillingQuantity(entry, activityName),
    remarks: getBillingRemarks(entry) || charge.name || "",
    source_module: "IPD",
  };
  // Prefer medical_service_id (charge-capture/services/) when present; fall back to service_code.
  if (charge.medical_service_id) {
    payload.medical_service_id = charge.medical_service_id;
  } else if (charge.service_code) {
    payload.service_code = charge.service_code;
  }
  if (charge.charge_modifiable === true && charge.rate != null) {
    payload.unit_price = charge.rate;
  }

  if (isReferredToMePatient) {
    if (referredByProviderId) {
      payload.referred_by_provider_id = referredByProviderId;
    }
    payload.payout_eligible = true;
    payload.calculate_doctor_payout = true;
    return payload;
  }

  payload.calculate_doctor_payout = true;
  return payload;
};

// [BILLING] + [LAB_TEST] Removes internal __billing_charge before care_plans/ POST (via onSubmit).
// __billing_charge is read for billing + lab tests in handleSubmit before this strip runs on care plan payload.
const stripBillingMetaFromEntry = (entry) => {
  if (!entry || typeof entry !== "object" || Array.isArray(entry)) return entry;
  const { __billing_charge: _billing, ...rest } = entry;
  return rest;
};

// [BILLING] Strips __billing_charge from single entry or multi-entry array payload.
const stripBillingMetaFromPayload = (payload) => {
  if (Array.isArray(payload)) {
    return payload.map((entry) => stripBillingMetaFromEntry(entry));
  }
  return stripBillingMetaFromEntry(payload);
};

// [LAB_TEST] Recursively removes the internal __service_meta helper (added on
// investigation table rows to carry medical_service_id/SNOMED code) so it never
// reaches the care_plans/ payload. Read for lab tests in handleSubmit first.
const stripServiceMetaDeep = (node) => {
  if (Array.isArray(node)) return node.map(stripServiceMetaDeep);
  if (node && typeof node === "object") {
    const { __service_meta: _serviceMeta, ...rest } = node;
    Object.keys(rest).forEach((key) => {
      rest[key] = stripServiceMetaDeep(rest[key]);
    });
    return rest;
  }
  return node;
};

// [BILLING] admissionId prop is required for billing API calls (passed from PatientDetailsPanel).
// [LAB_TEST] careContextId + doctorName come from ipd_admission list via PatientDetailsPanel.
const NurseOrderActivityModal = ({
  open,
  onClose,
  structureDef,
  onSubmit,
  admissionId,
  careContextId,
  doctorName,
  orderingDoctorId = "",
  orderingDoctorName = "",
  headerSlot = null,
  title = "",
  isReferredToMePatient = false,
  referredByProviderId = "",
}) => {
  const fields = useMemo(() => structureDef?.structure || [], [structureDef]);
  const activityName = String(structureDef?.name || "")
    .toLowerCase()
    .trim();
  const isMultiEntry =
    structureDef?.multi_entry === true ||
    structureDef?.multiEntry === true ||
    Number(structureDef?.multi_entry) === 1 ||
    Number(structureDef?.multiEntry) === 1 ||
    activityName === "medication" ||
    activityName === "task" ||
    activityName === "test";
  const createEmptyEntry = useMemo(() => () => buildInitialValues(fields), [fields]);
  const ORDER_TABS = useMemo(() => [
    {
      label: "Medication",
      groupField: "medication_order",
      title: "Medication Order",
      subtitle: "Order medications and prescriptions",
      icon: <LocalPharmacyOutlinedIcon sx={{ fontSize: "1.4rem", color: "#6366f1" }} />,
      tabIcon: <LocalPharmacyOutlinedIcon />,
    },
    {
      label: "Investigation",
      groupField: "test_order",
      title: "Investigation Order",
      subtitle: "Order labs, imaging and diagnostics",
      icon: <BiotechOutlinedIcon sx={{ fontSize: "1.4rem", color: "#0ea5e9" }} />,
      tabIcon: <BiotechOutlinedIcon />,
    },
    {
      label: "Nutrition",
      groupField: "nutrition_order",
      title: "Nutrition Order",
      subtitle: "Order dietary and nutrition plans",
      icon: <RestaurantMenuIcon sx={{ fontSize: "1.4rem", color: "#22c55e" }} />,
      tabIcon: <RestaurantMenuIcon />,
    },
  ], []);

  const [formData, setFormData] = useState({});
  const [activeOrderTab, setActiveOrderTab] = useState(0);
  const [orderQueues, setOrderQueues] = useState({ medication_order: [], test_order: [], nutrition_order: [] });
  const [queuedEntries, setQueuedEntries] = useState([]);
  const [editingEntryIndex, setEditingEntryIndex] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const activityTableConfig = useMemo(
    () => getTableConfigForActivity(structureDef?.name),
    [structureDef?.name]
  );

  const isAppointmentActivity = activityName === "appointment";
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [doctorOptions, setDoctorOptions] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  // Previous orders fetched from care_plan_data for quick reuse
  const [prevMedications, setPrevMedications] = useState([]);
  const [prevTests, setPrevTests] = useState([]);
  const [prevNutrition, setPrevNutrition] = useState([]);
  const [prevMedLoading, setPrevMedLoading] = useState(false);
  const [prevOrdersFetched, setPrevOrdersFetched] = useState(false);
  // [BILLING] Charge-master search state — keyed by field path (e.g. "medication.name").
  const [chargeOptionsByKey, setChargeOptionsByKey] = useState({});
  const [chargeLoadingByKey, setChargeLoadingByKey] = useState({});
  const chargeSearchTimersRef = useRef({});
  // [PHARMACY_RX] Stock check for the picked Medication drug name — keyed by field path,
  // same as chargeOptionsByKey. Populated by checkMedicationStock once a name is selected.
  const [medStockByKey, setMedStockByKey] = useState({});
  // [PHARMACY_RX] Which charge/medicine Autocomplete currently has its dropdown open
  // (field path, or null). Controlled so "Substitute" can pop the list open itself.
  const [openOptionsKey, setOpenOptionsKey] = useState(null);
  // [PHARMACY_RX] The pharmacy store every prescribe/search below is scoped to.
  // Auto-selected when the hospital has one; otherwise the picker rendered next
  // to "Reuse previous" in the Medication tab decides it.
  const pharmacyStore = usePharmacyStore();
  const pharmacyStoreId = pharmacyStore.storeId;
  // [PHARMACY_RX] Drug names whose stock check is still owed, keyed by field
  // path — see checkMedicationStock and the replay effect below it.
  const pendingStockChecksRef = useRef({});

  const getApiHeaders = useCallback(() => {
    const authToken = sessionStorage.getItem("accessToken") || "";
    return {
      Authorization: `Bearer ${authToken}`,
      "APP-MODE": sessionStorage.getItem("APP_MODE") || "",
      "ORG-CODE": sessionStorage.getItem("ORG_CODE") || "",
      "USER-ACCESS-KEY": sessionStorage.getItem("USER_ACCESS_KEY") || "",
    };
  }, []);

  useEffect(() => {
    if (open && isAppointmentActivity) {
      setLoadingDepartments(true);
      axios
        .get(
          `${process.env.REACT_APP_BACKEND_ROUTE_URL}get_all_medical_departments/`,
          { headers: getApiHeaders() }
        )
        .then((res) => {
          if (res.data && res.data.department_list) {
            const departments = res.data.department_list.map((dep) => {
              if (typeof dep === "string") return { departmentName: dep.trim(), departmentId: "" };
              const departmentName = dep?.department || dep?.name || dep?.department_name || "";
              return {
                departmentName: String(departmentName || "").trim(),
                departmentId: String(dep?.department_id || dep?.departmentId || dep?.id || "").trim(),
              };
            }).filter((d) => d.departmentName);
            setDepartmentOptions(departments);
          }
        })
        .catch((err) => console.error("Error fetching departments:", err))
        .finally(() => setLoadingDepartments(false));
    }
    if (!open) {
      setDepartmentOptions([]);
      setDoctorOptions([]);
    }
  }, [open, isAppointmentActivity, getApiHeaders]);

  const fetchDoctorsByDepartment = useCallback(
    (departmentName) => {
      if (!departmentName) {
        setDoctorOptions([]);
        return;
      }
      setLoadingDoctors(true);
      axios
        .post(
          `${process.env.REACT_APP_BACKEND_ROUTE_URL}get_all_doctors_based_on_department/`,
          { department: departmentName },
          { headers: getApiHeaders() }
        )
        .then((res) => {
          if (res.data && res.data.response_list) {
            setDoctorOptions(
              res.data.response_list.map((doc) => ({
                doctor_id: doc.doctor_id,
                name: doc.name,
              }))
            );
          } else {
            setDoctorOptions([]);
          }
        })
        .catch((err) => {
          console.error("Error fetching doctors:", err);
          setDoctorOptions([]);
        })
        .finally(() => setLoadingDoctors(false));
    },
    [getApiHeaders]
  );

  useEffect(() => {
    if (open) {
      setFormData(createEmptyEntry());
      setQueuedEntries([]);
      setOrderQueues({ medication_order: [], test_order: [], nutrition_order: [] });
      setEditingEntryIndex(null);
      setFieldErrors({});
      setActiveOrderTab(0);
      // [BILLING] Reset charge-master search cache when modal opens/closes.
      setChargeOptionsByKey({});
      setChargeLoadingByKey({});
      // Reset prev orders so fresh data is fetched next open
      setPrevMedications([]);
      setPrevTests([]);
      setPrevNutrition([]);
      setPrevOrdersFetched(false);
    }
  }, [open, isMultiEntry, createEmptyEntry]);

  // Auto-calculate total_quantity from MAEN + duration + unit.
  // Writes "" rather than bailing out when the regimen has no computable total:
  // returning early left the previous order's quantity on screen after the
  // duration was cleared, and submit_care_plan trusts this number.
  // TWO SHAPES, and the quantity must be written into the one the backend reads
  // for this template (manage_care_plan.medication_payload_for_entry):
  //   template 9 "orders"     -> cp_data.medication_order.medication
  //   template 3 "medication" -> cp_data.medication          (top level)
  // Handling only "orders" meant every nurse-placed medication order (template
  // 3) submitted with no quantity at all, so Prescription.details.total_quantity
  // stayed unset and medication_returnable/ reported the ward stock as unknown.
  useEffect(() => {
    const isOrders = activityName === "orders";
    const isMedication = activityName === "medication";
    if (!isOrders && !isMedication) return;
    const med = (isOrders ? formData?.medication_order?.medication : formData?.medication) || {};
    const quantity = totalQuantity(med);
    const totalQty = quantity === null ? "" : quantity;
    setFormData((prev) => {
      const prevMed = isOrders ? prev?.medication_order?.medication : prev?.medication;
      if (String(prevMed?.total_quantity ?? "") === String(totalQty)) return prev;
      if (isOrders) {
        return {
          ...prev,
          medication_order: {
            ...(prev.medication_order || {}),
            medication: { ...(prev.medication_order?.medication || {}), total_quantity: totalQty },
          },
        };
      }
      return {
        ...prev,
        medication: { ...(prev.medication || {}), total_quantity: totalQty },
      };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activityName,
    formData?.medication_order?.medication?.frequency,
    formData?.medication_order?.medication?.morning,
    formData?.medication_order?.medication?.afternoon,
    formData?.medication_order?.medication?.evening,
    formData?.medication_order?.medication?.night,
    formData?.medication_order?.medication?.duration,
    formData?.medication_order?.medication?.duration_unit,
    formData?.medication?.frequency,
    formData?.medication?.morning,
    formData?.medication?.afternoon,
    formData?.medication?.evening,
    formData?.medication?.night,
    formData?.medication?.duration,
    formData?.medication?.duration_unit,
  ]);

  // Lazy-fetch previous orders — called only when doctor opens any reuse dropdown
  const fetchPrevMedications = useCallback(() => {
    if (prevMedLoading || prevOrdersFetched) return;
    setPrevMedLoading(true);
    const carePlanId = Number(structureDef?.care_plan_id || structureDef?.id || structureDef?.template_id || 0);
    axios
      .post(`${process.env.REACT_APP_BACKEND_ROUTE_URL}care_plan_data/`, { care_plan_id: carePlanId, admission_id: admissionId }, { headers: getApiHeaders() })
      .then((r) => {
        const raw = Array.isArray(r.data?.data) ? r.data.data
          : Array.isArray(r.data?.results) ? r.data.results
          : Array.isArray(r.data) ? r.data
          : [];

        const meds = [];
        const tests = [];
        const nutritions = [];
        const seenMeds = new Set();
        const seenTests = new Set();

        raw.forEach((item) => {
          // Data is nested inside item.data; handle both plural (old) and singular (new) keys
          const orderData = item?.data || item;

          // ── Medications ──
          const medOrders = [
            ...(Array.isArray(orderData?.medication_orders) ? orderData.medication_orders : []),
            ...(Array.isArray(orderData?.medication_order) ? orderData.medication_order : []),
          ];
          medOrders.forEach((medOrder) => {
            if (!medOrder || typeof medOrder !== "object") return;
            const medObj = medOrder?.medication || (medOrder?.drug_name ? medOrder : null);
            if (!medObj) return;
            const drugName = medObj.drug_name || medObj.name || "";
            if (!drugName || seenMeds.has(drugName)) return;
            seenMeds.add(drugName);
            meds.push({
              label: drugName,
              priority: medOrder?.priority || "",
              medication: {
                drug_name: drugName,
                route: medObj.route || "",
                frequency: medObj.frequency || "",
                morning: medObj.morning ?? "",
                afternoon: medObj.afternoon ?? "",
                evening: medObj.evening ?? "",
                night: medObj.night ?? "",
                duration: medObj.duration ?? "",
                duration_unit: medObj.duration_unit || "",
                total_quantity: medObj.total_quantity ?? "",
                instructions: medObj.instructions || "",
                remark: medObj.remark || "",
                instruction_language: medObj.instruction_language || "",
                prescription_advice: medObj.prescription_advice || "",
                patient_advice: medObj.patient_advice || "",
                prescription_remark: medObj.prescription_remark || "",
              },
            });
          });

          // ── Tests / Investigations ──
          const testOrders = [
            ...(Array.isArray(orderData?.test_orders) ? orderData.test_orders : []),
            ...(Array.isArray(orderData?.test_order) ? orderData.test_order : []),
          ];
          testOrders.forEach((testOrder) => {
            if (!testOrder || typeof testOrder !== "object") return;
            const items = Array.isArray(testOrder?.test_items) ? testOrder.test_items : [];
            const label = items.map((i) => i?.test_name || "").filter(Boolean).join(", ") ||
              testOrder?.category || "";
            if (!label || seenTests.has(label)) return;
            seenTests.add(label);
            tests.push({ label, data: testOrder });
          });

          // ── Nutrition ──
          const nutritionOrders = [
            ...(Array.isArray(orderData?.nutrition_orders) ? orderData.nutrition_orders : []),
            ...(Array.isArray(orderData?.nutrition_order) ? orderData.nutrition_order : []),
          ];
          nutritionOrders.forEach((nutritionOrder) => {
            if (!nutritionOrder || typeof nutritionOrder !== "object") return;
            const label = nutritionOrder?.oral_diet?.diet_type || nutritionOrder?.priority || "Nutrition order";
            nutritions.push({ label, data: nutritionOrder });
          });
        });

        setPrevMedications(meds);
        setPrevTests(tests);
        setPrevNutrition(nutritions);
        setPrevOrdersFetched(true);
      })
      .catch(() => {
        setPrevMedications([]);
        setPrevTests([]);
        setPrevNutrition([]);
      })
      .finally(() => setPrevMedLoading(false));
  }, [getApiHeaders, prevMedLoading, prevOrdersFetched, structureDef, admissionId]);

  // [BILLING] Clear pending debounce timers on unmount.
  useEffect(() => {
    const timers = chargeSearchTimersRef.current;
    return () => {
      Object.values(timers).forEach((timerId) => clearTimeout(timerId));
    };
  }, []);

  const fetchChargeMasterOptions = useCallback(
    async (searchKey, name, chargeType) => {
      const trimmedName = String(name || "").trim();
      if (!trimmedName) {
        setChargeOptionsByKey((prev) => ({ ...prev, [searchKey]: [] }));
        return;
      }

      setChargeLoadingByKey((prev) => ({ ...prev, [searchKey]: true }));
      try {
        // [PHARMACY_RX] Medication drug-name typeahead uses search_medicines/ (SNOMED term
        // search, matches brand + generic synonyms) instead of search_charge_master/ — it has
        // no service_code, so items from this branch never populate __billing_charge (see
        // setBillingChargeSelection). Stock/OOS is checked separately once a name is picked
        // (checkMedicationStock), via inventory/pharmacy/prescribe/search/.
        let items;
        if (chargeType === "MEDICATION") {
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_ROUTE_URL}search_medicines/`,
            {
              headers: getApiHeaders(),
              params: { name: trimmedName },
            }
          );
          const data = Array.isArray(response?.data?.data) ? response.data.data : [];
          items = data.slice(0, 20).map((med) => ({
            name: med?.term || "",
            id: med?.conceptId ?? "",
            // [PHARMACY_RX] Composition of the picked medicine ("Paracetamol (500mg)").
            // This is what the Substitute lookup searches on — see checkMedicationStock.
            generic_name: med?.generic_name || "",
            _raw: med,
          })).filter((med) => med.name);
        } else {
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_ROUTE_URL}search_charge_master/`,
            {
              headers: getApiHeaders(),
              params: {
                name: trimmedName,
                type: chargeType,
                limit: 20,
              },
            }
          );
          items = Array.isArray(response?.data?.items) ? response.data.items : [];
        }
        setChargeOptionsByKey((prev) => ({ ...prev, [searchKey]: items }));
      } catch (error) {
        console.error("Error searching charge master:", error);
        setChargeOptionsByKey((prev) => ({ ...prev, [searchKey]: [] }));
      } finally {
        setChargeLoadingByKey((prev) => ({ ...prev, [searchKey]: false }));
      }
    },
    [getApiHeaders]
  );

  // [BILLING] Debounces fetchChargeMasterOptions — change CHARGE_SEARCH_DEBOUNCE_MS to adjust delay.
  const scheduleChargeMasterSearch = useCallback(
    (searchKey, name, chargeType) => {
      if (chargeSearchTimersRef.current[searchKey]) {
        clearTimeout(chargeSearchTimersRef.current[searchKey]);
      }
      chargeSearchTimersRef.current[searchKey] = setTimeout(() => {
        fetchChargeMasterOptions(searchKey, name, chargeType);
      }, CHARGE_SEARCH_DEBOUNCE_MS);
    },
    [fetchChargeMasterOptions]
  );

  // [PHARMACY_RX] Replaces a searchKey's options with same-composition substitutes,
  // triggered from the "Substitute" button once checkMedicationStock finds the picked
  // brand out of stock. Everything the catalogue returns for the generic is kept —
  // dropping the out-of-stock ones left the doctor staring at an empty dropdown when
  // the whole composition is short — but in-stock alternatives sort to the top, and
  // picking any of them re-runs the stock check anyway.
  const fetchSubstituteOptions = useCallback(
    async (searchKey, genericName) => {
      if (!genericName) return;
      if (!pharmacyStoreId) {
        toast.info("Select a pharmacy store first.");
        return;
      }
      setChargeLoadingByKey((prev) => ({ ...prev, [searchKey]: true }));
      try {
        const items = await fetchPharmacyMedicationOptions(getApiHeaders, {
          genericName,
          locationId: pharmacyStoreId,
        });
        const ranked = [...items].sort(
          (a, b) => Number(b.has_stock !== false) - Number(a.has_stock !== false),
        );
        setChargeOptionsByKey((prev) => ({ ...prev, [searchKey]: ranked }));
        if (ranked.length === 0) {
          toast.info(`No substitutes found for ${genericName}.`);
          return;
        }
        // The list is useless if the dropdown stays shut — open it on the field
        // the doctor just clicked Substitute on.
        setOpenOptionsKey(searchKey);
      } catch (error) {
        console.error("Error searching medicine substitutes:", error);
        toast.error("Could not load substitutes. Please try again.");
      } finally {
        setChargeLoadingByKey((prev) => ({ ...prev, [searchKey]: false }));
      }
    },
    [getApiHeaders, pharmacyStoreId]
  );

  // [PHARMACY_RX] search_medicines/ (the typeahead) doesn't carry stock, so once a drug
  // name is picked, look it up against inventory/pharmacy/prescribe/search/ (q=name) to
  // decide whether to show the red "Out of Stock" badge + Substitute button.
  const checkMedicationStock = useCallback(
    async (searchKey, drugName, selectedGenericName = "") => {
      const trimmed = String(drugName || "").trim();
      if (!trimmed) {
        delete pendingStockChecksRef.current[searchKey];
        setMedStockByKey((prev) => ({ ...prev, [searchKey]: null }));
        return;
      }
      // [PHARMACY_RX] Remembered so picking the pharmacy store AFTER the drug
      // name — the normal order when the hospital has several stores — can
      // replay this check instead of leaving the badge permanently blank.
      pendingStockChecksRef.current[searchKey] = {
        drugName: trimmed,
        genericName: selectedGenericName,
      };
      // No store picked yet → the stock answer would belong to no counter, so
      // leave the badge off entirely rather than showing a wrong one.
      if (!pharmacyStoreId) {
        setMedStockByKey((prev) => ({ ...prev, [searchKey]: null }));
        return;
      }
      setMedStockByKey((prev) => ({ ...prev, [searchKey]: { checking: true } }));
      const brandQuery = stripDoseForm(trimmed);
      // [PHARMACY_RX] The composition to substitute on is the one carried by the
      // medicine the doctor actually picked ("Paracetamol (500mg)" → "Paracetamol
      // 500mg"). Searching substitutes by brand instead returns other strengths of
      // the same brand ("Calpol" → Calpol 1000mg), which is not the same
      // composition — so the picked generic wins over anything derived from the name.
      const pickedGeneric = normalizeGenericTerm(selectedGenericName);
      try {
        const items = await fetchPharmacyMedicationOptions(getApiHeaders, {
          q: brandQuery,
          locationId: pharmacyStoreId,
        });
        const match = items.find((it) => it.name.toLowerCase() === trimmed.toLowerCase())
          || items.find((it) => it.name.toLowerCase().startsWith(brandQuery.toLowerCase()))
          || items[0]
          || null;
        setMedStockByKey((prev) => ({
          ...prev,
          [searchKey]: {
            checked: true,
            drugName: trimmed,
            hasStock: match ? match.has_stock !== false : false,
            // Picked composition first; then the catalogue's own generic for the
            // matched item; the brand name is only a last resort so the Substitute
            // button always has something to search on.
            genericName: pickedGeneric || match?.generic_name || brandQuery,
          },
        }));
      } catch {
        setMedStockByKey((prev) => ({ ...prev, [searchKey]: null }));
      }
    },
    [getApiHeaders, pharmacyStoreId]
  );

  // [PHARMACY_RX] With several pharmacy stores the doctor usually types the drug
  // name first and picks the store second — that first check was skipped for
  // want of a location_id, so replay it once a store exists. Without this the
  // Out of Stock badge never appears until the drug name is re-picked.
  useEffect(() => {
    if (!pharmacyStoreId) return;
    Object.entries(pendingStockChecksRef.current).forEach(
      ([searchKey, { drugName, genericName }]) => {
        checkMedicationStock(searchKey, drugName, genericName);
      }
    );
  }, [pharmacyStoreId, checkMedicationStock]);

  // [BILLING] + [LAB_TEST] When user picks a charge-master / charge-capture item, attach metadata.
  const setBillingChargeSelection = useCallback((onFieldChange, selectedItem) => {
    const serviceCode = selectedItem?.service_code ?? selectedItem?.code ?? "";
    const medicalServiceId = selectedItem?.medical_service_id ?? selectedItem?.id ?? "";
    if (serviceCode || medicalServiceId) {
      onFieldChange(["__billing_charge"], {
        service_code: serviceCode || undefined,
        name: selectedItem.name,
        // [LAB_TEST] Fields for add_patient_lab_tests/ test[] payload.
        code: serviceCode,
        medical_service_id: medicalServiceId,
        rate: selectedItem.rate ?? selectedItem.fee ?? null,
        charge_modifiable: selectedItem.chargeModifiable === true || selectedItem.charge_modifiable === true,
        quantity_modifiable: selectedItem.quantityModifiable !== false && selectedItem.quantity_modifiable !== false,
      });
      return;
    }
    onFieldChange(["__billing_charge"], null);
  }, []);

  // [LAB_TEST] Builds test[] for add_patient_lab_tests/ from entries.
  //   • Standalone "test" activity → charge-master selection on the entry root
  //     (__billing_charge).
  //   • "orders" → Investigation tab → medical-service selections captured on
  //     each table row under __service_meta, nested inside
  //     test_order[].test_items[]. We walk the structure to find them.
  const buildLabTestsFromEntries = useCallback((entries) => {
    const list = Array.isArray(entries) ? entries : [entries];
    const tests = [];
    const seen = new Set();
    const TEST_ITEM_NAME_FIELDS = [
      "test_name",
      "name",
      "investigation",
      "test",
      "order_detail",
      "orderDetail",
    ];

    const pushTest = (name, code, medicalServiceId, priority) => {
      const trimmedName = String(name || "").trim();
      if (!trimmedName) return;
      const dedupeKey = String(medicalServiceId || code || trimmedName);
      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);
      tests.push({
        name: trimmedName,
        code: String(code || ""),
        medical_service_id: String(medicalServiceId ?? ""),
        // add_patient_lab_tests/ creates one order per test[] entry, and both dashboards
        // read priority off the order (IpdLabOrders/IpdRadiologyOrders mapApiOrder), so it
        // belongs on the item. The form collects one priority for the whole order, which
        // is why every test from a single save carries the same value.
        priority: toWirePriority(priority),
      });
    };

    // Charge-master selection (Test activity).
    list.forEach((entry) => {
      const charge = entry?.__billing_charge;
      if (charge?.name) {
        pushTest(charge.name, charge.code || charge.service_code, charge.medical_service_id, entry?.priority);
      }
    });

    const processTestItemRow = (row, orderPriority) => {
      if (!row || typeof row !== "object") return;
      // Fields already covered by __service_meta carry the real code/
      // medical_service_id — skip them in the plain-field fallback below so a
      // row with a service selection doesn't get pushed twice (once with the
      // real id, once with an empty one from the bare display-text field).
      const metaFields = new Set();
      if (row.__service_meta && typeof row.__service_meta === "object") {
        Object.entries(row.__service_meta).forEach(([field, meta]) => {
          metaFields.add(field);
          if (meta?.name) {
            pushTest(meta.name, meta.code, meta.medical_service_id, orderPriority);
          }
        });
      }
      const plainField = TEST_ITEM_NAME_FIELDS.find(
        (field) =>
          !metaFields.has(field) &&
          row[field] != null &&
          String(row[field]).trim() !== "",
      );
      if (plainField) {
        pushTest(
          row[plainField],
          row.code ?? row.service_code ?? row.snomed_code ?? "",
          row.medical_service_id ?? row.medicalServiceId ?? row.id ?? "",
          orderPriority,
        );
      }
    };

    // Orders → Investigation: __service_meta on dynamic-select rows, plus plain
    // test_items.test_name rows (reuse previous / saved care plan data).
    // Priority is carried down rather than read at the row: the Investigation form puts
    // one Priority select on the test_order group, while the tests themselves live a level
    // deeper in test_items[] / __service_meta.
    const collectInvestigations = (node, inheritedPriority) => {
      if (!node || typeof node !== "object") return;
      if (Array.isArray(node)) {
        node.forEach((child) => collectInvestigations(child, inheritedPriority));
        return;
      }

      const priority = String(node.priority || "").trim() || inheritedPriority;

      if (Array.isArray(node.test_items)) {
        node.test_items.forEach((row) => processTestItemRow(row, priority));
      } else if (node.__service_meta && typeof node.__service_meta === "object") {
        Object.values(node.__service_meta).forEach((meta) => {
          if (meta?.name) {
            pushTest(meta.name, meta.code, meta.medical_service_id, priority);
          }
        });
      }

      Object.entries(node).forEach(([key, child]) => {
        if (key === "test_items") return;
        collectInvestigations(child, priority);
      });
    };
    collectInvestigations(list);

    return tests;
  }, []);

  // [LAB_TEST] POST add_patient_lab_tests/ after care plan save succeeds.
  // Fires for the standalone "test" activity and for investigations ordered
  // via Orders → Investigation so they appear on the lab dashboard.
  const postPatientLabTests = useCallback(
    async (entries) => {
      if (activityName !== "test" && activityName !== "orders") return;

      const tests = buildLabTestsFromEntries(entries);
      if (tests.length === 0) {
        console.warn(
          "[LAB_TEST] Skipped add_patient_lab_tests — no investigations with test metadata.",
        );
        return;
      }

      if (!careContextId) {
        console.warn("[LAB_TEST] Skipped add_patient_lab_tests — missing care_context_id.");
        toast.warn(
          "Care plan saved, but lab order was not created (missing care context).",
        );
        return;
      }
      if (!admissionId) {
        console.warn("[LAB_TEST] Skipped add_patient_lab_tests — missing admission id.");
        return;
      }

      // [NURSE] A nurse orders ON BEHALF OF a doctor (CPOE D2), so the logged-in
      // user is NOT the ordering clinician. The doctor console's fallback chain
      // here was `doctorName || sessionStorage.doctorName || sessionStorage.userName`,
      // which on a nurse login resolves to the NURSE and would attribute her lab
      // order to herself. orderingDoctorName comes from the on-behalf picker and
      // is the only acceptable source; with none resolved we do not guess.
      const resolvedDoctorName = String(orderingDoctorName || "").trim();
      if (!resolvedDoctorName) {
        console.warn("[LAB_TEST][NURSE] Skipped add_patient_lab_tests — no ordering doctor resolved.");
        toast.warn("Order saved, but the lab order was not created (no ordering doctor selected).");
        return;
      }

      // One Care Plan save → one lab batch on the dashboard. A later save for
      // the same patient gets a new id and shows as its own row.
      const labTestBatchId = createLabOrderBatchId();
      const testPayload = tests.map((t) => ({
        ...t,
        lab_test_batch_id: labTestBatchId,
      }));

      await axios.post(
        `${process.env.REACT_APP_BACKEND_ROUTE_URL}add_patient_lab_tests/`,
        {
          care_context_id: careContextId,
          admission_id: String(admissionId),
          patient_type: "IPD",
          // Doctor-ordered investigations enter the lab dashboard as NEW.
          status: activityName === "orders" ? "NEW" : "PAID",
          doctor_name: resolvedDoctorName,
          lab_test_batch_id: labTestBatchId,
          // Order-level, alongside status — a priority sent only inside test[] came back
          // empty on the lab dashboard, and status (the other order-wide attribute) lives
          // here too. The form collects one priority per order, so this loses nothing.
          priority: testPayload[0]?.priority || "Routine",
          test: testPayload,
        },
        { headers: getApiHeaders() }
      );
      rememberLabOrderBatch(admissionId, labTestBatchId, testPayload);
    },
    [activityName, admissionId, buildLabTestsFromEntries, careContextId, orderingDoctorName, getApiHeaders]
  );


  // [BILLING] Posts charges after care plan save.
  // - 1 entry with __billing_charge  → POST ipd_admission/{id}/billing/charges/
  // - 2+ entries with __billing_charge → POST ipd_admission/{id}/billing/charge-capture/bulk-post/
  // Only entries where user selected from charge-master dropdown are billed (has service_code).
  const postBillingCharges = useCallback(
    async (entries) => {
      if (!admissionId) return;

      const billableEntries = entries.filter(
        (entry) =>
          entry?.__billing_charge?.medical_service_id ||
          entry?.__billing_charge?.service_code
      );
      if (billableEntries.length === 0) return;

      const baseUrl = `${process.env.REACT_APP_BACKEND_ROUTE_URL}ipd_admission/${admissionId}/billing`;

      // [BILLING] Single charge — payout fields depend on Referred To Me vs admitting list.
      if (billableEntries.length === 1) {
        const entry = billableEntries[0];
        await axios.post(
          `${baseUrl}/charges/`,
          buildSingleBillingChargePayload({
            entry,
            activityName,
            isReferredToMePatient,
            referredByProviderId,
          }),
          { headers: getApiHeaders() }
        );
        return;
      }

      // [BILLING] Multiple charges (Add To Activity queue) — bulk-post; prefer medical_service_id.
      await axios.post(
        `${baseUrl}/charge-capture/bulk-post/`,
        {
          charges: billableEntries.map((entry) => {
            const charge = entry.__billing_charge;
            const row = {
              quantity: parseBillingQuantity(entry, activityName),
            };
            if (charge.medical_service_id) {
              row.medical_service_id = charge.medical_service_id;
            } else if (charge.service_code) {
              row.service_code = charge.service_code;
            }
            return row;
          }),
        },
        { headers: getApiHeaders() }
      );
    },
    [admissionId, activityName, getApiHeaders, isReferredToMePatient, referredByProviderId]
  );

  const handleChange = (path, value) => {
    setFormData((prev) => updateNestedValue(prev, path, value));
    const pathKey = path.join(".");
    setFieldErrors((prev) => {
      if (!prev[pathKey]) return prev;
      const next = { ...prev };
      delete next[pathKey];
      return next;
    });
  };

  const applyFieldErrors = (errors) => {
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // [BILLING] Queued entries retain __billing_charge; bulk billing uses all queued on Save.
  const handleAddToActivity = () => {
    setQueuedEntries((prev) => {
      if (editingEntryIndex === null) {
        return [...prev, formData];
      }
      return prev.map((entry, idx) => (idx === editingEntryIndex ? formData : entry));
    });
    setEditingEntryIndex(null);
    setFormData(createEmptyEntry());
    setFieldErrors({});
  };

  const isOrderGroupEmpty = (data) => {
    if (!data || typeof data !== "object") return true;
    return Object.values(data).every((v) => {
      if (v === "" || v === null || v === undefined || v === false) return true;
      if (Array.isArray(v)) return v.length === 0;
      if (typeof v === "object") return isOrderGroupEmpty(v);
      return false;
    });
  };

  const getOrderEntrySummary = (groupField, entry) => {
    if (groupField === "medication_order") {
      return { cols: [
        { label: "Drug Name", value: entry?.medication?.drug_name || entry?.medication?.name || "—" },
        { label: "Route", value: entry?.medication?.route || "—" },
        { label: "Frequency", value: entry?.medication?.frequency || "—" },
        { label: "Priority", value: entry?.priority || "—" },
      ]};
    }
    if (groupField === "test_order") {
      const testCount = Array.isArray(entry?.test_items) ? entry.test_items.length : 0;
      return { cols: [
        { label: "Category", value: toLabel(entry?.category || "—") },
        { label: "Tests", value: testCount > 0 ? `${testCount} test${testCount !== 1 ? "s" : ""}` : "—" },
        { label: "Priority", value: entry?.priority || "—" },
      ]};
    }
    if (groupField === "nutrition_order") {
      return { cols: [
        { label: "Diet Type", value: entry?.oral_diet?.diet_type || entry?.diet_type || "—" },
        { label: "Priority", value: entry?.priority || "—" },
        { label: "Intent", value: entry?.intent || "—" },
      ]};
    }
    return { cols: [] };
  };

  const handleOrderAddToActivity = () => {
    const activeTab = ORDER_TABS[activeOrderTab];
    if (!activeTab) return;
    const groupData = formData[activeTab.groupField] || {};

    if (isOrderGroupEmpty(groupData)) {
      toast.error("Please fill in at least one field before adding.");
      return;
    }

    setOrderQueues((prev) => ({
      ...prev,
      [activeTab.groupField]: [...prev[activeTab.groupField], { ...groupData }],
    }));
    // Reset only this tab's group fields
    const groupFieldDef = fields.find((f) => f.field === activeTab.groupField);
    const emptyGroup = buildInitialValues(groupFieldDef?.structure || []);
    setFormData((prev) => ({ ...prev, [activeTab.groupField]: emptyGroup }));
    setFieldErrors({});
  };

  const handleOrderEditQueued = (groupField, index) => {
    const entry = orderQueues[groupField]?.[index];
    if (!entry) return;
    setFormData((prev) => ({ ...prev, [groupField]: { ...entry } }));
    setOrderQueues((prev) => ({
      ...prev,
      [groupField]: prev[groupField].filter((_, i) => i !== index),
    }));
  };

  const handleOrderRemoveQueued = (groupField, index) => {
    setOrderQueues((prev) => ({
      ...prev,
      [groupField]: prev[groupField].filter((_, i) => i !== index),
    }));
  };

  const handleEditQueued = (indexToEdit) => {
    const entry = queuedEntries[indexToEdit];
    if (!entry) return;
    setFormData(entry);
    setEditingEntryIndex(indexToEdit);
    setFieldErrors({});
  };

  const handleRemoveQueued = (indexToRemove) => {
    setQueuedEntries((prev) => prev.filter((_, idx) => idx !== indexToRemove));
    if (editingEntryIndex === indexToRemove) {
      setEditingEntryIndex(null);
      setFormData(createEmptyEntry());
    } else if (editingEntryIndex !== null && indexToRemove < editingEntryIndex) {
      setEditingEntryIndex((prev) => (prev === null ? null : prev - 1));
    }
  };

  const hasCurrentDraftData = !isEmptyValue(stripBillingMetaFromEntry(formData));

  const getSubmitPayload = () => {
    if (activityName === "orders") {
      // Auto-include any filled-in form data that wasn't explicitly queued via ADD TO ACTIVITY
      const medQueue = [...orderQueues.medication_order];
      const testQueue = [...orderQueues.test_order];
      const nutQueue = [...orderQueues.nutrition_order];
      ORDER_TABS.forEach((tab) => {
        const groupData = formData[tab.groupField];
        if (!groupData || isOrderGroupEmpty(groupData)) return;
        if (tab.groupField === "medication_order") medQueue.push({ ...groupData });
        else if (tab.groupField === "test_order") testQueue.push({ ...groupData });
        else if (tab.groupField === "nutrition_order") nutQueue.push({ ...groupData });
      });
      return {
        medication_order: medQueue,
        test_order: testQueue,
        nutrition_order: nutQueue,
      };
    }
    if (!isMultiEntry) return formData;
    const queuedWithEditsApplied =
      editingEntryIndex === null
        ? [...queuedEntries]
        : queuedEntries.map((entry, idx) => (idx === editingEntryIndex ? formData : entry));

    if (hasCurrentDraftData && editingEntryIndex === null) {
      return [...queuedWithEditsApplied, formData];
    }
    return queuedWithEditsApplied;
  };

  const validateCurrentForm = () =>
    applyFieldErrors(validateEntryRequiredFields(formData, fields));

  const validateSubmitPayload = () => {
    if (activityName === "orders") {
      // Use getSubmitPayload which already auto-includes any current draft
      const payload = getSubmitPayload();
      const total =
        payload.medication_order.length +
        payload.test_order.length +
        payload.nutrition_order.length;
      if (total === 0) {
        toast.error("Please fill in at least one order before saving.");
        return false;
      }
      return true;
    }

    const payload = getSubmitPayload();
    const entries = Array.isArray(payload) ? payload : [payload];

    if (
      entries.length === 0 ||
      entries.every((entry) => isEmptyValue(stripBillingMetaFromEntry(entry)))
    ) {
      validateCurrentForm();
      toast.error("Please fill in all required fields.");
      return false;
    }

    for (let index = 0; index < entries.length; index += 1) {
      const entryErrors = validateEntryRequiredFields(entries[index], fields);
      if (Object.keys(entryErrors).length > 0) {
        if (isMultiEntry) {
          toast.error(
            `Entry ${index + 1} is missing required fields. Please edit and complete it.`
          );
        } else {
          toast.error("Please fill in all required fields.");
        }

        const isCurrentFormEntry =
          !isMultiEntry ||
          index === editingEntryIndex ||
          (editingEntryIndex === null && index === queuedEntries.length && hasCurrentDraftData);

        if (isCurrentFormEntry) {
          applyFieldErrors(entryErrors);
        }
        return false;
      }
    }

    setFieldErrors({});
    return true;
  };

  const handleAddToActivityWithValidation = () => {
    if (!validateCurrentForm()) {
      toast.error("Please fill in all required fields before adding.");
      return;
    }
    handleAddToActivity();
  };

  const renderField = (field, sourceData, onFieldChange, pathPrefix = []) => {
    const path = [...pathPrefix, field.field];
    const fieldValue = getNestedValue(sourceData, path);
    const label = toLabel(field.field);
    const pathKey = path.join(".");
    const fieldError = fieldErrors[pathKey] || "";
    const isRequired = isFieldRequired(field);

    if (field.type === "object") {
      // Special layout for medication object: compact rows for frequency+doses and duration+unit
      const DOSE_FIELDS = ["morning", "afternoon", "evening", "night"];
      const DOSE_LABELS = { morning: "M", afternoon: "A", evening: "E", night: "N" };
      const isMedicationObj = field.field === "medication";

      const structure = field.structure || [];
      const freqField = isMedicationObj ? structure.find((f) => f.field === "frequency") : null;
      const doseFields = isMedicationObj ? structure.filter((f) => DOSE_FIELDS.includes(f.field)) : [];
      const durationField = isMedicationObj ? structure.find((f) => f.field === "duration") : null;
      const durationUnitField = isMedicationObj ? structure.find((f) => f.field === "duration_unit") : null;

      return (
        <Grid item xs={12} key={path.join(".")}>
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              {label}
              {isRequired ? " *" : ""}
            </Typography>
            <Grid container spacing={1.5}>
              {isMedicationObj
                ? structure.map((nestedField) => {
                    // Skip dose fields — rendered inside the frequency row
                    if (DOSE_FIELDS.includes(nestedField.field)) return null;
                    // Skip duration_unit — rendered next to duration
                    if (nestedField.field === "duration_unit") return null;

                    // Frequency + M/A/E/N compact row
                    if (nestedField.field === "frequency" && freqField) {
                      return (
                        <Grid item xs={12} key="freq-dose-row">
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box sx={{ minWidth: 130, flexShrink: 0 }}>
                              <FormControl fullWidth size="small" required={isFieldRequired(freqField)}>
                                <InputLabel required={isFieldRequired(freqField)}>Frequency</InputLabel>
                                <Select
                                  label="Frequency"
                                  value={getNestedValue(sourceData, [...path, "frequency"]) ?? ""}
                                  onChange={(e) => onFieldChange([...path, "frequency"], e.target.value)}
                                >
                                  {(freqField.options || []).map((opt) => (
                                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Box>
                            {doseFields.map((df) => (
                              <Box key={df.field} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#6b7280", flexShrink: 0 }}>
                                  {DOSE_LABELS[df.field]}
                                </Typography>
                                <TextField
                                  size="small"
                                  type="number"
                                  value={getNestedValue(sourceData, [...path, df.field]) ?? ""}
                                  onChange={(e) => onFieldChange([...path, df.field], e.target.value)}
                                  inputProps={{ min: 0, style: { width: 44, textAlign: "center", padding: "6px 4px" } }}
                                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
                                />
                              </Box>
                            ))}
                          </Box>
                        </Grid>
                      );
                    }

                    // Duration + Duration Unit compact row
                    if (nestedField.field === "duration" && durationField && durationUnitField) {
                      return (
                        <Grid item xs={12} key="duration-row">
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <TextField
                              size="small"
                              type="number"
                              label="Duration"
                              required={isFieldRequired(durationField)}
                              value={getNestedValue(sourceData, [...path, "duration"]) ?? ""}
                              onChange={(e) => onFieldChange([...path, "duration"], e.target.value)}
                              inputProps={{ min: 0 }}
                              sx={{ flex: 1 }}
                            />
                            <FormControl size="small" required={isFieldRequired(durationUnitField)} sx={{ minWidth: 130 }}>
                              <InputLabel required={isFieldRequired(durationUnitField)}>Unit</InputLabel>
                              <Select
                                label="Unit"
                                value={getNestedValue(sourceData, [...path, "duration_unit"]) ?? ""}
                                onChange={(e) => onFieldChange([...path, "duration_unit"], e.target.value)}
                              >
                                {(durationUnitField.options || []).map((opt) => (
                                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                        </Grid>
                      );
                    }

                    // Instructions — dropdown (meal-timing options), not free text
                    if (nestedField.field === "instructions") {
                      return (
                        <Grid item xs={12} sm={6} key={[...path, "instructions"].join(".")}>
                          <FormControl fullWidth size="small" required={isFieldRequired(nestedField)}>
                            <InputLabel required={isFieldRequired(nestedField)}>Instructions</InputLabel>
                            <Select
                              label="Instructions"
                              value={getNestedValue(sourceData, [...path, "instructions"]) ?? ""}
                              onChange={(e) => onFieldChange([...path, "instructions"], e.target.value)}
                            >
                              {MEDICATION_INSTRUCTION_OPTIONS.map((opt) => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      );
                    }

                    return renderField(nestedField, sourceData, onFieldChange, path);
                  })
                : structure.map((nestedField) =>
                    renderField(nestedField, sourceData, onFieldChange, path)
                  )}
            </Grid>
          </Paper>
        </Grid>
      );
    }

    if (field.type === "group") {
      // For "orders" activity all groups are always visible (no dropdown selection needed).
      // For other activities, only show a group when its condition matches.
      if (field.condition && activityName !== "orders") {
        const condCurrentValue = getNestedValue(sourceData, [field.condition.field]);
        if (condCurrentValue !== field.condition.value) return null;
      }
      return (
        <Grid item xs={12} key={path.join(".")}>
          <Paper
            variant="outlined"
            sx={{ p: 2, mt: 0.5, borderRadius: 2 }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, mb: 1.5, color: "#121959", fontSize: "0.88rem" }}
            >
              {field.label || label}
            </Typography>
            <Grid container spacing={1.5}>
              {(field.structure || []).map((nestedField) =>
                renderField(nestedField, sourceData, onFieldChange, path)
              )}
            </Grid>
          </Paper>
        </Grid>
      );
    }

    if (field.type === "boolean") {
      return (
        <Grid item xs={12} key={path.join(".")}>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(fieldValue)}
                onChange={(event) => onFieldChange(path, event.target.checked)}
              />
            }
            label={label}
          />
        </Grid>
      );
    }

    if (field.type === "select") {
      // Hide the order_type selector for "orders" — all groups are shown at once
      if (activityName === "orders" && field.field === "order_type") return null;

      const fieldNameLower = String(field.field || "").toLowerCase();
      const isAppointmentSpecialty = isAppointmentActivity && fieldNameLower === "specialty";
      const isAppointmentDoctor = isAppointmentActivity && fieldNameLower === "doctor";

      if (isAppointmentSpecialty) {
        return (
          <Grid item xs={12} sm={6} key={path.join(".")}>
            <FormControl fullWidth size="small" required={isRequired} error={Boolean(fieldError)}>
              <InputLabel required={isRequired}>{label}</InputLabel>
              <Select
                label={label}
                value={fieldValue ?? ""}
                onChange={(event) => {
                  const selectedDeptName = event.target.value;
                  onFieldChange(path, selectedDeptName);
                  const doctorPath = [...pathPrefix, "doctor"];
                  onFieldChange(doctorPath, "");
                  fetchDoctorsByDepartment(selectedDeptName);
                }}
                endAdornment={loadingDepartments ? <CircularProgress size={20} sx={{ mr: 2 }} /> : null}
              >
                {departmentOptions.map((dep) => (
                  <MenuItem key={dep.departmentName} value={dep.departmentName}>
                    {dep.departmentName}
                  </MenuItem>
                ))}
              </Select>
              {fieldError ? (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {fieldError}
                </Typography>
              ) : null}
            </FormControl>
          </Grid>
        );
      }

      if (isAppointmentDoctor) {
        return (
          <Grid item xs={12} sm={6} key={path.join(".")}>
            <FormControl fullWidth size="small" required={isRequired} error={Boolean(fieldError)}>
              <InputLabel required={isRequired}>{label}</InputLabel>
              <Select
                label={label}
                value={fieldValue ?? ""}
                onChange={(event) => onFieldChange(path, event.target.value)}
                disabled={doctorOptions.length === 0 && !loadingDoctors}
                endAdornment={loadingDoctors ? <CircularProgress size={20} sx={{ mr: 2 }} /> : null}
              >
                {doctorOptions.map((doc) => (
                  <MenuItem key={doc.doctor_id} value={doc.name}>
                    {doc.name}
                  </MenuItem>
                ))}
              </Select>
              {fieldError ? (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {fieldError}
                </Typography>
              ) : null}
            </FormControl>
          </Grid>
        );
      }

      return (
        <Grid item xs={12} sm={6} key={path.join(".")}>
          <FormControl fullWidth size="small" required={isRequired} error={Boolean(fieldError)}>
            <InputLabel required={isRequired}>{label}</InputLabel>
            <Select
              label={label}
              value={fieldValue ?? ""}
              onChange={(event) => onFieldChange(path, event.target.value)}
            >
              {(field.options || []).map((option) => (
                <MenuItem key={option} value={option}>
                  {toLabel(option)}
                </MenuItem>
              ))}
            </Select>
            {fieldError ? (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {fieldError}
              </Typography>
            ) : null}
          </FormControl>
        </Grid>
      );
    }

    if (field.type === "multi-select") {
      return (
        <Grid item xs={12} sm={6} key={path.join(".")}>
          <FormControl fullWidth size="small" required={isRequired} error={Boolean(fieldError)}>
            <InputLabel required={isRequired}>{label}</InputLabel>
            <Select
              multiple
              label={label}
              value={Array.isArray(fieldValue) ? fieldValue : []}
              onChange={(event) => onFieldChange(path, event.target.value)}
              renderValue={(selected) => selected.map((item) => toLabel(item)).join(", ")}
            >
              {(field.options || []).map((option) => (
                <MenuItem key={option} value={option}>
                  {toLabel(option)}
                </MenuItem>
              ))}
            </Select>
            {fieldError ? (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {fieldError}
              </Typography>
            ) : null}
          </FormControl>
        </Grid>
      );
    }

    if (field.type === "radio") {
      return (
        <Grid item xs={12} key={path.join(".")}>
          <FormControl required={isRequired} error={Boolean(fieldError)}>
            <FormLabel sx={{ fontSize: "0.82rem", fontWeight: 500, color: "#374151", mb: 0.5 }}>
              {label}{isRequired ? " *" : ""}
            </FormLabel>
            <RadioGroup
              row
              value={fieldValue ?? ""}
              onChange={(e) => onFieldChange(path, e.target.value)}
            >
              {(field.options || []).map((opt) => (
                <FormControlLabel
                  key={opt}
                  value={opt}
                  control={<Radio size="small" sx={{ color: "#21cdc0", "&.Mui-checked": { color: "#21cdc0" } }} />}
                  label={<Typography sx={{ fontSize: "0.83rem" }}>{toLabel(opt)}</Typography>}
                />
              ))}
            </RadioGroup>
            {fieldError && <Typography variant="caption" color="error">{fieldError}</Typography>}
          </FormControl>
        </Grid>
      );
    }

    if (field.type === "datetime") {
      const selected = parseDateValue(fieldValue);
      return (
        <Grid item xs={12} sm={6} key={path.join(".")}>
          <DatePicker
            selected={selected}
            onChange={(date) =>
              onFieldChange(path, date ? formatDateForApi(date, true) : "")
            }
            showTimeSelect
            timeIntervals={15}
            dateFormat="dd-MM-yyyy h:mm aa"
            timeCaption="Time"
            placeholderText="dd-mm-yyyy --:--"
            customInput={
              <DatePickerTextField
                label={label}
                endIcon={<DateTimeFieldEndIcon />}
                required={isRequired}
                error={Boolean(fieldError)}
                helperText={fieldError || " "}
              />
            }
            popperProps={CARE_PLAN_DATE_PICKER_POPPER}
            isClearable
          />
        </Grid>
      );
    }

    if (field.type === "date") {
      const selected = parseDateValue(fieldValue);
      return (
        <Grid item xs={12} sm={6} key={path.join(".")}>
          <DatePicker
            selected={selected}
            onChange={(date) =>
              onFieldChange(path, date ? formatDateForApi(date, false) : "")
            }
            dateFormat="dd-MM-yyyy"
            placeholderText="dd-mm-yyyy"
            customInput={
              <DatePickerTextField
                label={label}
                endIcon={<CalendarTodayIcon sx={{ fontSize: 20 }} />}
                required={isRequired}
                error={Boolean(fieldError)}
                helperText={fieldError || " "}
              />
            }
            popperProps={CARE_PLAN_DATE_PICKER_POPPER}
            isClearable
          />
        </Grid>
      );
    }

    if (field.type === "time") {
      const selectedTime = parseTimeFieldValue(fieldValue);
      return (
        <Grid item xs={12} sm={6} key={path.join(".")}>
          <DatePicker
            selected={selectedTime}
            onChange={(date) => onFieldChange(path, date ? formatTimeForApi(date) : "")}
            showTimeSelect
            showTimeSelectOnly
            timeIntervals={15}
            timeCaption="Time"
            dateFormat="h:mm aa"
            customInput={
              <DatePickerTextField
                label={label}
                endIcon={<AccessTimeIcon sx={{ fontSize: 20 }} />}
                required={isRequired}
                error={Boolean(fieldError)}
                helperText={fieldError || " "}
              />
            }
            popperProps={CARE_PLAN_DATE_PICKER_POPPER}
            isClearable
          />
        </Grid>
      );
    }

    if (field.type === "table") {
      const rows = Array.isArray(fieldValue) ? fieldValue : [];
      const columns = field.columns || [];
      const emptyTableRow = () =>
        columns.reduce((acc, col) => ({ ...acc, [col.field]: col.type === "boolean" ? false : "" }), {});

      const handleAddTableRow = () =>
        onFieldChange(path, [...rows, emptyTableRow()]);

      const handleRemoveTableRow = (rowIdx) =>
        onFieldChange(path, rows.filter((_, i) => i !== rowIdx));

      const handleCellChange = (rowIdx, colField, value) =>
        onFieldChange(
          path,
          rows.map((row, i) => (i === rowIdx ? { ...row, [colField]: value } : row))
        );

      // [LAB_TEST] When a medical service is picked in a dynamic-select cell
      // (Orders → Investigation), stash its id/code alongside the display text
      // so the add_patient_lab_tests/ payload can carry medical_service_id +
      // service code — not just the name string. Also keep charge-capture flags
      // so amount/qty can be locked when the modal exposes those fields.
      const handleCellSelectService = (rowIdx, colField, value, item) =>
        onFieldChange(
          path,
          rows.map((row, i) => {
            if (i !== rowIdx) return row;
            const meta = { ...(row.__service_meta || {}) };
            const serviceId = item?.medical_service_id ?? item?.id ?? "";
            const code = String(
              item?.service_code ??
                item?.code ??
                item?.conceptId ??
                item?.concept_id ??
                item?.snomed_code ??
                "",
            );
            if (serviceId || code || item?.name || item?.term) {
              meta[colField] = {
                name: item?.name || item?.term || value,
                medical_service_id: String(serviceId),
                code,
                rate: item?.rate ?? item?.fee ?? null,
                charge_modifiable: item?.chargeModifiable === true || item?.charge_modifiable === true,
                quantity_modifiable: item?.quantityModifiable !== false && item?.quantity_modifiable !== false,
              };
            } else {
              delete meta[colField];
            }
            return { ...row, [colField]: value, __service_meta: meta };
          })
        );

      return (
        <Grid item xs={12} key={path.join(".")}>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.8 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#121959" }}>
                {label}{isRequired ? " *" : ""}
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddTableRow}
                sx={{ fontSize: "0.73rem", textTransform: "none", color: "#21cdc0" }}
              >
                Add Row
              </Button>
            </Box>

            {rows.length > 0 ? (
              <Paper variant="outlined" sx={{ borderRadius: 1, overflow: "hidden" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f5fffe" }}>
                      {columns.map((col) => (
                        <TableCell
                          key={col.field}
                          sx={{ fontWeight: 700, fontSize: "0.72rem", color: "#374151", py: 0.8, whiteSpace: "nowrap" }}
                        >
                          {toLabel(col.field)}
                          {col.required && (
                            <Typography component="span" sx={{ color: "error.main", ml: 0.3 }}>*</Typography>
                          )}
                        </TableCell>
                      ))}
                      <TableCell sx={{ width: 36 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, rowIdx) => (
                      <TableRow key={rowIdx}>
                        {columns.map((col) => {
                          // Boolean column → compact checkbox
                          if (col.type === "boolean") {
                            return (
                              <TableCell key={col.field} sx={{ py: 0.4, px: 1, textAlign: "center" }}>
                                <Checkbox
                                  size="small"
                                  checked={Boolean(row[col.field])}
                                  onChange={(e) => handleCellChange(rowIdx, col.field, e.target.checked)}
                                  sx={{ p: 0, color: "#21cdc0", "&.Mui-checked": { color: "#21cdc0" } }}
                                />
                              </TableCell>
                            );
                          }
                          // Select column → dropdown with static options
                          if (col.type === "select") {
                            return (
                              <TableCell key={col.field} sx={{ py: 0.4, px: 1, minWidth: 160 }}>
                                <FormControl fullWidth size="small">
                                  <Select
                                    displayEmpty
                                    value={row[col.field] ?? ""}
                                    onChange={(e) => handleCellChange(rowIdx, col.field, e.target.value)}
                                    sx={{ fontSize: "0.78rem" }}
                                  >
                                    <MenuItem value="" disabled sx={{ fontSize: "0.78rem" }}>
                                      <em>Select…</em>
                                    </MenuItem>
                                    {(col.options || []).map((opt) => (
                                      <MenuItem key={opt} value={opt} sx={{ fontSize: "0.78rem" }}>
                                        {toLabel(opt)}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </TableCell>
                            );
                          }
                          // Dynamic-select column → freeSolo Autocomplete (searches medical services)
                          if (col.type === "dynamic-select") {
                            const dynKey = `${path.join(".")}.${rowIdx}.${col.field}`;
                            // Backend can return multiple records with the same display name
                            // (e.g. duplicate charge-master rows) — dedupe so one match doesn't
                            // eat every slot in the suggestion list.
                            const dynOpts = Array.from(
                              new Set((chargeOptionsByKey[dynKey] || []).map((i) => i.name).filter(Boolean))
                            );
                            const isLoading = Boolean(chargeLoadingByKey[dynKey]);
                            // [ORDERS_INVESTIGATION] Category (Laboratory/Radiology/Other) decides
                            // which service_type the test-name search hits below. Typing stays
                            // enabled even with no category picked, but the search is withheld
                            // and a red helper text prompts for the category instead. Only groups
                            // whose schema actually has a "category" field are gated.
                            const groupData = getNestedValue(sourceData, pathPrefix) || {};
                            const hasCategoryField = Object.prototype.hasOwnProperty.call(groupData, "category");
                            const categoryRequired = hasCategoryField && !groupData.category;
                            return (
                              <TableCell key={col.field} sx={{ py: 0.4, px: 1, minWidth: 180 }}>
                                <Autocomplete
                                  freeSolo
                                  size="small"
                                  fullWidth
                                  options={dynOpts}
                                  loading={isLoading}
                                  value={row[col.field] ?? ""}
                                  onInputChange={(_, val) => {
                                    handleCellChange(rowIdx, col.field, val);
                                    if (categoryRequired) return;
                                    if (!val || val.length < 2) return;
                                    const categoryLower = String(groupData.category || "").toLowerCase();
                                    // Patient-aware charge-capture search: admission_id + group_code
                                    // (Laboratory → LABORATORY, Radiology → RADIOLOGY). "Other" omits
                                    // group_code so the list stays bed-scoped but not group-scoped.
                                    const groupCode = investigationCategoryToGroupCode(categoryLower);
                                    setChargeLoadingByKey((p) => ({ ...p, [dynKey]: true }));
                                    searchChargeCaptureServices(val, {
                                      limit: 50,
                                      admissionId,
                                      groupCode,
                                    })
                                      .then((items) => {
                                        setChargeOptionsByKey((p) => ({
                                          ...p,
                                          [dynKey]: items.map((i) => ({
                                            ...i,
                                            name: i.name || i.term || i.service_name || i.label || "",
                                            medical_service_id: i.medical_service_id ?? i.id,
                                            service_code: i.service_code ?? i.code,
                                            code: i.service_code ?? i.code,
                                          })),
                                        }));
                                      })
                                      .catch(() => {})
                                      .finally(() => setChargeLoadingByKey((p) => ({ ...p, [dynKey]: false })));
                                  }}
                                  onChange={(_, val) => {
                                    const selectedName = typeof val === "string" ? val : (val || "");
                                    const picked = (chargeOptionsByKey[dynKey] || []).find(
                                      (i) => (i.name || i.term || "") === selectedName
                                    );
                                    handleCellSelectService(rowIdx, col.field, selectedName, picked);
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      size="small"
                                      required={col.required}
                                      placeholder={`Search ${toLabel(col.field)}…`}
                                      error={categoryRequired}
                                      helperText={categoryRequired ? "Select category first" : ""}
                                      onBlur={(e) => {
                                        params.onBlur?.(e);
                                        // [LAB_TEST] freeSolo lets a doctor type the full name and click away
                                        // without ever choosing the dropdown row, which skips onChange entirely
                                        // and leaves __service_meta (medical_service_id/code) unset. If the typed
                                        // text exactly matches a fetched result, attach its metadata here too.
                                        const currentVal = row[col.field] || "";
                                        if (!currentVal || row.__service_meta?.[col.field]) return;
                                        const matched = (chargeOptionsByKey[dynKey] || []).find(
                                          (i) => (i.name || i.term || "") === currentVal
                                        );
                                        if (matched) handleCellSelectService(rowIdx, col.field, currentVal, matched);
                                      }}
                                      InputProps={{ ...params.InputProps, endAdornment: isLoading ? <CircularProgress size={14} /> : params.InputProps.endAdornment }}
                                      sx={{
                                        "& .MuiInputBase-input": { fontSize: "0.78rem", py: "5px" },
                                        "& .MuiFormHelperText-root": { fontSize: "0.68rem", color: "#ef4444", mx: 0, mt: 0.3 },
                                      }}
                                    />
                                  )}
                                />
                              </TableCell>
                            );
                          }
                          // Default: text / number / date — lock qty/amount when the
                          // selected charge-capture service says they aren't modifiable.
                          const serviceMeta =
                            row.__service_meta &&
                            typeof row.__service_meta === "object"
                              ? Object.values(row.__service_meta)[0]
                              : null;
                          const fieldKey = String(col.field || "").toLowerCase();
                          const isQtyField = ["qty", "quantity"].includes(fieldKey);
                          const isAmountField = ["amount", "rate", "fee", "charge", "unit_price"].includes(fieldKey);
                          const lockQty =
                            Boolean(serviceMeta) &&
                            isQtyField &&
                            serviceMeta.quantity_modifiable === false;
                          const lockAmount =
                            Boolean(serviceMeta) &&
                            isAmountField &&
                            serviceMeta.charge_modifiable !== true;
                          const locked = lockQty || lockAmount;
                          return (
                            <TableCell key={col.field} sx={{ py: 0.4, px: 1 }}>
                              <TextField
                                size="small"
                                fullWidth
                                type={col.type === "date" ? "date" : col.type === "number" ? "number" : "text"}
                                value={
                                  isAmountField && (row[col.field] == null || row[col.field] === "") && serviceMeta?.rate != null
                                    ? serviceMeta.rate
                                    : (row[col.field] ?? "")
                                }
                                onChange={(e) => handleCellChange(rowIdx, col.field, e.target.value)}
                                required={col.required}
                                disabled={locked}
                                InputProps={locked ? { readOnly: true } : undefined}
                                InputLabelProps={col.type === "date" ? { shrink: true } : undefined}
                                sx={{
                                  "& .MuiInputBase-input": {
                                    fontSize: "0.78rem",
                                    py: "5px",
                                    ...(locked ? { backgroundColor: "#F9FAFB" } : {}),
                                  },
                                }}
                              />
                            </TableCell>
                          );
                        })}
                        <TableCell sx={{ py: 0.4, px: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveTableRow(rowIdx)}
                            sx={{ color: "#ef4444", "&:hover": { backgroundColor: "#fef2f2" } }}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: "1rem" }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            ) : (
              <Box
                onClick={handleAddTableRow}
                sx={{
                  border: "1px dashed #d1d5db", borderRadius: 1, p: 1.5,
                  textAlign: "center", cursor: "pointer",
                  "&:hover": { borderColor: "#21cdc0", backgroundColor: "#f5fffe" },
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Click "+ Add Row" to add {label.toLowerCase()} entries
                </Typography>
              </Box>
            )}
            {fieldError && (
              <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
                {fieldError}
              </Typography>
            )}
          </Box>
        </Grid>
      );
    }

    const isMultiline = field.type === "textarea";
    const inputType = field.type === "number" ? "number" : "text";
    // [BILLING] Charge-master autocomplete replaces plain TextField for Medication/Test fields.
    const chargeSearchConfig = getChargeSearchConfig(field, activityName, pathPrefix);

    if (chargeSearchConfig
      // && !isMultiline 
      && inputType === "text") {
      const searchKey = path.join(".");
      const chargeItems = chargeOptionsByKey[searchKey] || [];
      // Substitutes carry a `label` (brand + composition in brackets); charge-master
      // and search_medicines items have none, so they fall back to their plain name.
      const chargeOptions = chargeItems.map((item) => item.label || item.name).filter(Boolean);
      const findChargeItem = (value) =>
        chargeItems.find((item) => (item.label || item.name) === value);
      const isChargeSearchLoading = Boolean(chargeLoadingByKey[searchKey]);
      const isMedicationField = chargeSearchConfig.type === "MEDICATION";
      // [PHARMACY_RX] Set by checkMedicationStock once a Medication drug name is picked.
      const medStock = isMedicationField ? medStockByKey[searchKey] : null;
      const isOutOfStock = Boolean(medStock?.checked && medStock.hasStock === false);

      return (
        <Grid item xs={12} sm={6} key={path.join(".")}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
            <Autocomplete
              freeSolo
              fullWidth
              size="small"
              options={chargeOptions}
              loading={isChargeSearchLoading}
              value={fieldValue ?? ""}
              // Options are already the server's answer (search_medicines/,
              // search_charge_master/, or the substitute lookup). MUI's default
              // client filter matches them against the text in the box, which would
              // hide every substitute — the box still holds the out-of-stock brand.
              filterOptions={(opts) => opts}
              open={openOptionsKey === searchKey && chargeOptions.length > 0}
              onOpen={() => setOpenOptionsKey(searchKey)}
              onClose={() => setOpenOptionsKey(null)}
              onChange={(_, newValue) => {
                const selectedName = typeof newValue === "string" ? newValue : String(newValue || "");
                onFieldChange(path, selectedName);
                const matchedItem = findChargeItem(selectedName);
                setBillingChargeSelection(onFieldChange, matchedItem);
                setOpenOptionsKey(null);
                if (isMedicationField) {
                  // The composition of the picked medicine, stored as a sibling of
                  // the drug-name field so it lands in the same medicine object the
                  // backend resolves (medication.generic_name for template 3,
                  // medication_order.medication.generic_name for template 9 —
                  // medicine_details() reads that key either way). Until this was
                  // written, the generic was fetched for the substitute lookup and
                  // then thrown away, so nurse-placed orders reached the pharmacy
                  // with no generic at all while doctor-placed ones always had one.
                  onFieldChange([...path.slice(0, -1), "generic_name"], matchedItem?.generic_name || "");
                  checkMedicationStock(searchKey, selectedName, matchedItem?.generic_name);
                }
              }}
              onInputChange={(_, value, reason) => {
                if (reason === "reset") return;
                onFieldChange(path, value);
                const matchedItem = findChargeItem(value);
                setBillingChargeSelection(onFieldChange, matchedItem || null);
                scheduleChargeMasterSearch(searchKey, value, chargeSearchConfig.type);
                if (isMedicationField) {
                  // Typing over the picked drug invalidates both the badge and
                  // the replay entry — otherwise choosing a store later would
                  // re-check a name that is no longer in the box. The generic is
                  // re-derived from whatever the text now matches, so a half-typed
                  // name cannot leave the previous drug's composition behind.
                  onFieldChange([...path.slice(0, -1), "generic_name"], matchedItem?.generic_name || "");
                  delete pendingStockChecksRef.current[searchKey];
                  setMedStockByKey((prev) => ({ ...prev, [searchKey]: null }));
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={label}
                  required={isRequired}
                  error={Boolean(fieldError)}
                  helperText={fieldError || " "}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isOutOfStock && (
                          <Typography sx={{ fontSize: "0.66rem", fontWeight: 700, color: "#DC2626", whiteSpace: "nowrap", mr: 0.5 }}>
                            OUT OF STOCK
                          </Typography>
                        )}
                        {(isChargeSearchLoading || medStock?.checking) ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            {isOutOfStock && (
              <Button
                size="small"
                onClick={() => fetchSubstituteOptions(searchKey, medStock.genericName)}
                sx={{ mt: 0.5, flexShrink: 0, whiteSpace: "nowrap", fontSize: "0.72rem", fontWeight: 700, textTransform: "none", color: "#B45309", border: "1px solid #FDE68A", backgroundColor: "#FFFBEB", borderRadius: 1, px: 1.2, py: 0.3, "&:hover": { backgroundColor: "#FEF3C7" } }}
              >
                Substitute
              </Button>
            )}
          </Box>
        </Grid>
      );
    }

    return (
      <Grid item xs={12} sm={isMultiline ? 12 : 6} key={path.join(".")}>
        <TextField
          fullWidth
          size="small"
          label={label}
          type={inputType}
          value={fieldValue ?? ""}
          multiline={isMultiline}
          rows={isMultiline ? 3 : undefined}
          required={isRequired}
          error={Boolean(fieldError)}
          helperText={fieldError || " "}
          onChange={(event) => onFieldChange(path, event.target.value)}
        />
      </Grid>
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateSubmitPayload()) return;

    setIsSubmitting(true);
    try {
      const payload = getSubmitPayload();
      // [BILLING] Keep billing metadata for postBillingCharges; strip it before care_plans/ save.
      // [LAB_TEST] Likewise keep __service_meta for postPatientLabTests, then strip it.
      const billingEntries = Array.isArray(payload) ? payload : [payload];
      const stripped = stripServiceMetaDeep(stripBillingMetaFromPayload(payload));
      // [NURSE] D2: every nurse-placed order names the doctor it is placed on
      // behalf of. submit_care_plan/ reads cp_data.ordering_doctor_id to resolve
      // the prescriber and REJECTS a medication entry outright when it cannot
      // (it does not guess), so this must ride on every entry, not the envelope.
      const withOrderingDoctor = (entry) =>
        entry && typeof entry === "object" && !Array.isArray(entry)
          ? { ...entry, ordering_doctor_id: orderingDoctorId }
          : entry;
      const carePlanPayload = Array.isArray(stripped)
        ? stripped.map(withOrderingDoctor)
        : withOrderingDoctor(stripped);
      const result = await onSubmit(carePlanPayload);
      if (result !== false) {
        // [BILLING] Billing runs after care plan save; failure is logged but does not block close.
        try {
          await postBillingCharges(billingEntries);
        } catch (billingError) {
          console.error("Failed to post billing charges:", billingError);
        }
        // [LAB_TEST] Lab test order runs after care plan + billing (Test + Orders → Investigation).
        try {
          await postPatientLabTests(billingEntries);
        } catch (labTestError) {
          console.error("Failed to post patient lab tests:", labTestError);
          toast.warn("Care plan saved, but lab order could not be created. Please retry from Orders.");
        }
        // [PHARMACY_RX] No prescription POST here — submit_care_plan/ already wrote
        // the order book row and its dose tasks. See the header note.
        onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 600 }}>
        {title || structureDef?.name || "Care Plan Activity"}
      </DialogTitle>
      <form onSubmit={handleSubmit} noValidate>
        <DialogContent dividers sx={{ maxHeight: "70vh" }}>
          {/* [NURSE] Order-type and on-behalf-doctor pickers, owned by the
              wrapper because changing either swaps structureDef, which this
              component takes as a prop. Rendered inside the same dialog so the
              nurse sees one form, not a pre-step. */}
          {headerSlot}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {isMultiEntry && (
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Added to activity: {queuedEntries.length}
                </Typography>
              </Box>
            )}
            {activityName === "orders" ? (() => {
              const activeTab = ORDER_TABS[activeOrderTab];
              const groupField = fields.find((f) => f.field === activeTab?.groupField);
              const queuedCount = (orderQueues[activeTab?.groupField] || []).length;
              return (
                <Box>
                  {/* Tab bar — segmented control */}
                  <Box
                    sx={{
                      display: "flex",
                      backgroundColor: "#f1f3f5",
                      borderRadius: "10px",
                      p: "4px",
                      mb: 2,
                      gap: "4px",
                    }}
                  >
                    {ORDER_TABS.map((t, i) => {
                      const isActive = activeOrderTab === i;
                      const tabQueued = (orderQueues[t.groupField] || []).length;
                      return (
                        <Box
                          key={i}
                          onClick={() => setActiveOrderTab(i)}
                          sx={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.8,
                            py: "9px",
                            px: 1,
                            borderRadius: "7px",
                            cursor: "pointer",
                            backgroundColor: isActive ? "#ffffff" : "transparent",
                            boxShadow: isActive ? "0 1px 4px rgba(0,0,0,0.12)" : "none",
                            transition: "all 0.15s",
                            position: "relative",
                            "&:hover": {
                              backgroundColor: isActive ? "#ffffff" : "rgba(255,255,255,0.5)",
                            },
                          }}
                        >
                          <Box sx={{ color: isActive ? "#21cdc0" : "#6b7280", display: "flex", fontSize: 18 }}>
                            {t.tabIcon}
                          </Box>
                          <Typography sx={{ fontSize: "0.85rem", fontWeight: isActive ? 700 : 500, color: isActive ? "#0e3f66" : "#6b7280", whiteSpace: "nowrap" }}>
                            {t.label}
                          </Typography>
                          {tabQueued > 0 && (
                            <Box sx={{
                              backgroundColor: "#21cdc0", color: "#fff",
                              borderRadius: "10px", fontSize: "0.65rem", fontWeight: 700,
                              px: 0.7, lineHeight: "18px", minWidth: 18, textAlign: "center",
                            }}>
                              {tabQueued}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>

                  {/* Section header */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.5, px: 0.5, borderBottom: "1px solid #f0f4f8" }}>
                    <Box sx={{
                      width: 44, height: 44, borderRadius: 2,
                      backgroundColor: "#f8fafc",
                      border: "1px solid #e4e9ef",
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}>
                      {activeTab.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: "#121959" }}>
                        {activeTab.title}
                      </Typography>
                      <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                        {activeTab.subtitle}
                      </Typography>
                    </Box>
                    <Chip
                      label={`${queuedCount} added`}
                      size="small"
                      sx={{
                        fontSize: "0.72rem", fontWeight: 600,
                        backgroundColor: queuedCount > 0 ? "#EBFAF9" : "#f0f4f8",
                        color: queuedCount > 0 ? "#0a7a73" : "#6b7280",
                      }}
                    />
                  </Box>

                  {/* Tab content */}
                  <Box sx={{ pt: 2 }}>
                    {/* Reuse previous — medication */}
                    {activeTab.groupField === "medication_order" && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
                        {/* [NURSE] The pharmacy store picker is deliberately NOT
                            rendered here — a nurse orders from the ward, not a
                            counter. usePharmacyStore is still mounted below so
                            stock/substitute lookups resolve against the default
                            store; only the control is gone. */}
                        <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#111827", whiteSpace: "nowrap", flexShrink: 0 }}>
                          Reuse previous:
                        </Typography>
                        <Autocomplete
                          size="small"
                          options={prevMedications}
                          getOptionLabel={(opt) => opt.label || ""}
                          loading={prevMedLoading}
                          sx={{
                            width: 260,
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": { borderColor: "#21cdc0", borderWidth: 1.5 },
                              "&:hover fieldset": { borderColor: "#0e9e94" },
                              "&.Mui-focused fieldset": { borderColor: "#0E3F66", borderWidth: 2 },
                            },
                          }}
                          onOpen={fetchPrevMedications}
                          onChange={(_, selected) => {
                            if (!selected) return;
                            setFormData((prev) => ({
                              ...prev,
                              medication_order: {
                                ...(prev.medication_order || {}),
                                priority: selected.priority || prev.medication_order?.priority || "",
                                medication: {
                                  ...(prev.medication_order?.medication || {}),
                                  ...selected.medication,
                                },
                              },
                            }));
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select medication…"
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: prevMedLoading
                                  ? <CircularProgress size={14} />
                                  : params.InputProps.endAdornment,
                              }}
                            />
                          )}
                        />
                      </Box>
                    )}
                    {/* Reuse previous — investigation */}
                    {activeTab.groupField === "test_order" && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                        <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#111827", whiteSpace: "nowrap", flexShrink: 0 }}>
                          Reuse previous:
                        </Typography>
                        <Autocomplete
                          size="small"
                          options={prevTests}
                          getOptionLabel={(opt) => opt.label || ""}
                          loading={prevMedLoading}
                          sx={{
                            width: 260,
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": { borderColor: "#21cdc0", borderWidth: 1.5 },
                              "&:hover fieldset": { borderColor: "#0e9e94" },
                              "&.Mui-focused fieldset": { borderColor: "#0E3F66", borderWidth: 2 },
                            },
                          }}
                          onOpen={fetchPrevMedications}
                          onChange={(_, selected) => {
                            if (!selected) return;
                            setFormData((prev) => ({
                              ...prev,
                              test_order: {
                                ...(prev.test_order || {}),
                                ...selected.data,
                              },
                            }));
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select investigation…"
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: prevMedLoading
                                  ? <CircularProgress size={14} />
                                  : params.InputProps.endAdornment,
                              }}
                            />
                          )}
                        />
                      </Box>
                    )}
                    {/* Reuse previous — nutrition */}
                    {activeTab.groupField === "nutrition_order" && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                        <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#111827", whiteSpace: "nowrap", flexShrink: 0 }}>
                          Reuse previous:
                        </Typography>
                        <Autocomplete
                          size="small"
                          options={prevNutrition}
                          getOptionLabel={(opt) => opt.label || ""}
                          loading={prevMedLoading}
                          sx={{
                            width: 260,
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": { borderColor: "#21cdc0", borderWidth: 1.5 },
                              "&:hover fieldset": { borderColor: "#0e9e94" },
                              "&.Mui-focused fieldset": { borderColor: "#0E3F66", borderWidth: 2 },
                            },
                          }}
                          onOpen={fetchPrevMedications}
                          onChange={(_, selected) => {
                            if (!selected) return;
                            setFormData((prev) => ({
                              ...prev,
                              nutrition_order: {
                                ...(prev.nutrition_order || {}),
                                ...selected.data,
                              },
                            }));
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select nutrition order…"
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: prevMedLoading
                                  ? <CircularProgress size={14} />
                                  : params.InputProps.endAdornment,
                              }}
                            />
                          )}
                        />
                      </Box>
                    )}
                    <Grid container spacing={1.5}>
                      {groupField
                        ? (groupField.structure || []).map((f) =>
                            renderField(f, formData, handleChange, [activeTab.groupField])
                          )
                        : null}
                    </Grid>
                  </Box>

                  {/* Queued entries for this tab */}
                  {queuedCount > 0 && (
                    <Box sx={{ mt: 2, borderTop: "1px solid #e4e9ef", pt: 1.5 }}>
                      <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#374151", mb: 1 }}>
                        Added Entries ({queuedCount})
                      </Typography>
                      {/* Column header row */}
                      {(() => {
                        const { cols } = getOrderEntrySummary(activeTab.groupField, {});
                        if (!cols.length) return null;
                        return (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, mb: 0.5 }}>
                            <Typography sx={{ fontSize: "0.67rem", fontWeight: 700, color: "#9ca3af", width: 20, flexShrink: 0 }}>#</Typography>
                            {cols.map((col) => (
                              <Typography key={col.label} sx={{ fontSize: "0.67rem", fontWeight: 700, color: "#9ca3af", flex: 1 }}>
                                {col.label.toUpperCase()}
                              </Typography>
                            ))}
                            <Box sx={{ width: 64, flexShrink: 0 }} />
                          </Box>
                        );
                      })()}

                      {/* Entry rows */}
                      {(orderQueues[activeTab.groupField] || []).map((entry, idx) => {
                        const { cols } = getOrderEntrySummary(activeTab.groupField, entry);
                        return (
                          <Box
                            key={idx}
                            sx={{
                              display: "flex", alignItems: "center", gap: 1,
                              px: 1.5, py: 0.6, mb: 0.5, borderRadius: 1,
                              backgroundColor: "#f8fafc", border: "1px solid #e4e9ef",
                            }}
                          >
                            <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#9ca3af", width: 20, flexShrink: 0 }}>
                              {idx + 1}
                            </Typography>
                            {cols.map((col) => (
                              <Typography
                                key={col.label}
                                title={String(col.value || "")}
                                sx={{ fontSize: "0.78rem", color: col.value && col.value !== "—" ? "#1e293b" : "#d1d5db", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                              >
                                {col.value || "—"}
                              </Typography>
                            ))}
                            <Box sx={{ display: "flex", gap: 0, flexShrink: 0 }}>
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOrderEditQueued(activeTab.groupField, idx)}
                                  sx={{ color: "#21cdc0", p: 0.3, "&:hover": { backgroundColor: "#EBFAF9" } }}
                                >
                                  <EditOutlinedIcon sx={{ fontSize: "0.9rem" }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Remove">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOrderRemoveQueued(activeTab.groupField, idx)}
                                  sx={{ color: "#ef4444", p: 0.3, "&:hover": { backgroundColor: "#fef2f2" } }}
                                >
                                  <DeleteOutlineIcon sx={{ fontSize: "0.9rem" }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              );
            })() : (
              <Grid container spacing={1.5}>
                {fields.map((field) => renderField(field, formData, handleChange))}
              </Grid>
            )}
            {isMultiEntry && queuedEntries.length > 0 && (
              <Paper variant="outlined" sx={{ mt: 1.5, p: 1.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Added Activities
                </Typography>
                {activityTableConfig ? (
                  <Box sx={{ overflowX: "auto" }}>
                    <Box
                      sx={{
                        minWidth: 560,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: `repeat(${activityTableConfig.columns.length}, minmax(120px, 1fr)) 140px`,
                          columnGap: 1,
                          px: 1,
                        }}
                      >
                        {activityTableConfig.columns.map((col) => (
                          <Box key={`head-${col.key}`} sx={{ py: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>
                              {col.label}
                            </Typography>
                          </Box>
                        ))}
                        <Box sx={{ py: 0.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>
                            Action
                          </Typography>
                        </Box>
                      </Box>

                      {queuedEntries.map((entry, index) => {
                        const rowData = activityTableConfig.getRow(entry, index);
                        return (
                          <Box
                            key={`row-${index}`}
                            sx={{
                              display: "grid",
                              gridTemplateColumns: `repeat(${activityTableConfig.columns.length}, minmax(120px, 1fr)) 140px`,
                              columnGap: 1,
                              alignItems: "center",
                              px: 1,
                              py: 1,
                              borderRadius: 1,
                              backgroundColor: "grey.50",
                            }}
                          >
                            {activityTableConfig.columns.map((col) => (
                              <Box key={`row-${index}-${col.key}`} sx={{ minWidth: 0 }}>
                                <Typography variant="body2" noWrap title={displayOrDash(rowData[col.key])}>
                                  {displayOrDash(rowData[col.key])}
                                </Typography>
                              </Box>
                            ))}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                              <Chip
                                label="Edit"
                                size="small"
                                color="primary"
                                variant="outlined"
                                onClick={() => handleEditQueued(index)}
                              />
                              <Chip
                                label="Remove"
                                size="small"
                                color="error"
                                variant="outlined"
                                onClick={() => handleRemoveQueued(index)}
                              />
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {queuedEntries.map((entry, index) => (
                      <Box
                        key={`queued-${index}`}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          px: 1.25,
                          py: 0.75,
                        }}
                      >
                        <Typography variant="body2">
                          {getEntryDisplayLabel(entry, structureDef?.name)}
                        </Typography>
                        <Chip
                          label="Remove"
                          size="small"
                          color="error"
                          variant="outlined"
                          onClick={() => handleRemoveQueued(index)}
                        />
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            )}
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          {(isMultiEntry || activityName === "orders") && (
            <Button
              variant="outlined"
              onClick={activityName === "orders" ? handleOrderAddToActivity : handleAddToActivityWithValidation}
              disabled={isSubmitting}
            >
              {editingEntryIndex !== null ? "Update Activity" : "Add To Activity"}
            </Button>
          )}
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            Save Activity
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default NurseOrderActivityModal;
